<template>
  <div class="aigc-image-page">
    <div class="page-head">
      <div>
        <h2>AI图片生成</h2>
        <p>选择模型能力、组合参考图，生成结果自动保存到素材库和 COS</p>
      </div>
      <div class="head-actions">
        <el-tag size="mini" type="success">{{ currentModelLabel }}</el-tag>
        <el-tag size="mini" type="info">{{ outputPlanText }}</el-tag>
        <el-button size="mini" icon="el-icon-refresh" :loading="assetLoading" @click="loadImageAssets">刷新素材</el-button>
      </div>
    </div>

    <section class="panel image-workspace">
      <div class="form-panel">
        <div class="panel-title">
          <span>生成设置</span>
          <div class="create-actions">
            <el-date-picker
                v-model="imageScheduleAt"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                placeholder="预约时间"
                size="mini"
                style="width: 180px">
            </el-date-picker>
            <el-button size="mini" icon="el-icon-time" :loading="scheduling" @click="scheduleImageGeneration">定时生成</el-button>
            <el-button type="primary" size="mini" icon="el-icon-picture-outline" :loading="generating" @click="generateImage">立即生成</el-button>
          </div>
        </div>

        <el-form ref="imageForm" :model="imageForm" :rules="imageRules" label-width="92px" size="mini" class="image-form">
          <el-row :gutter="16">
            <el-col :span="6">
              <el-form-item label="服务商" prop="provider">
                <el-select v-model="imageForm.provider" style="width: 100%" @change="handleProviderChange">
                  <el-option label="火山方舟图片 Seedream" value="SEEDREAM"></el-option>
                  <el-option label="公司 GPT Image 2" value="GPT_IMAGE"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="10">
              <el-form-item label="模型" prop="model">
                <el-select
                    v-model="imageForm.model"
                    filterable
                    allow-create
                    default-first-option
                    style="width: 100%"
                    placeholder="选择或输入模型 ID"
                    @change="handleModelChange">
                  <el-option
                      v-for="model in currentImageModelOptions"
                      :key="model.value"
                      :label="model.label"
                      :value="model.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <div class="capability-strip">
            <span class="capability-name">{{ currentModeConfig.label || '文生图' }}</span>
            <el-tag size="mini" :type="modeUsesReferences ? 'warning' : 'info'">{{ referenceLimitText }}</el-tag>
            <el-tag size="mini" type="info">{{ outputPlanText }}</el-tag>
            <el-tag v-if="currentModelCapability.supportsGroup" size="mini" type="success">组图</el-tag>
            <el-tag v-if="currentModelCapability.supportsLoopMultiple" size="mini" type="warning">循环生成</el-tag>
            <el-tag v-if="currentModelCapability.supportsLayerSplit" size="mini" type="success">图层拆分</el-tag>
            <el-tag v-if="currentModelCapability.supportsInteractiveEdit" size="mini" type="success">交互编辑</el-tag>
            <el-tag v-if="currentModelCapability.supportsWebSearch" size="mini" type="success">联网搜索</el-tag>
          </div>

          <el-form-item label="模式" prop="mode">
            <div class="mode-grid">
              <button
                  v-for="mode in currentModeOptions"
                  :key="mode.value"
                  type="button"
                  class="mode-card"
                  :class="{ active: imageForm.mode === mode.value }"
                  @click="selectMode(mode.value)">
                <span class="mode-title">{{ mode.label }}</span>
                <span class="mode-meta">{{ modeMeta(mode) }}</span>
              </button>
            </div>
          </el-form-item>

          <el-form-item label="提示词" prop="prompt">
            <el-input
                v-model="imageForm.prompt"
                type="textarea"
                :rows="6"
                :maxlength="currentPromptMaxLength"
                show-word-limit
                :placeholder="currentPromptPlaceholder">
            </el-input>
          </el-form-item>

          <el-form-item label="高级参数">
            <el-collapse v-model="activeAdvancedPanels" class="advanced-collapse">
              <el-collapse-item name="advanced">
                <template slot="title">
                  <span class="advanced-title">尺寸、种子和输出控制</span>
                </template>
                <el-row :gutter="16">
                  <el-col :span="6">
                    <el-form-item label="尺寸" label-width="64px">
                      <el-select v-model="imageForm.size" style="width: 100%">
                        <el-option v-for="size in currentImageSizeOptions" :key="size.value" :label="size.label" :value="size.value"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col v-if="currentModelCapability.supportsOutputFormat" :span="5">
                    <el-form-item label="格式" label-width="64px">
                      <el-select v-model="imageForm.outputFormat" clearable placeholder="默认" style="width: 100%">
                        <el-option label="PNG" value="png"></el-option>
                        <el-option label="JPEG" value="jpeg"></el-option>
                        <el-option v-if="isGptImageProvider" label="WEBP" value="webp"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="5">
                    <el-form-item label="输出" label-width="64px">
                      <el-input-number
                          v-model="imageForm.maxImages"
                          :min="1"
                          :max="currentMaxOutputImages"
                          :disabled="!currentAllowsMultipleOutputs"
                          controls-position="right"
                          style="width: 100%">
                      </el-input-number>
                    </el-form-item>
                  </el-col>
                  <el-col :span="5">
                    <el-form-item label="种子" label-width="64px">
                      <el-input-number v-model="imageForm.seed" :min="0" controls-position="right" style="width: 100%"></el-input-number>
                    </el-form-item>
                  </el-col>
                  <el-col :span="3">
                    <div class="switch-stack">
                      <span>水印</span>
                      <el-switch v-model="imageForm.watermark"></el-switch>
                    </div>
                    <div class="switch-stack">
                      <span>联网</span>
                      <el-switch v-model="imageForm.webSearch" :disabled="!currentModeConfig.webSearch"></el-switch>
                    </div>
                  </el-col>
                </el-row>
                <el-row v-if="isGptImageProvider" :gutter="16" class="advanced-extra-row">
                  <el-col :span="6">
                    <el-form-item label="质量" label-width="64px">
                      <el-select v-model="imageForm.quality" style="width: 100%">
                        <el-option label="Auto" value="auto"></el-option>
                        <el-option label="Medium" value="medium"></el-option>
                        <el-option label="High" value="high"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                  <el-col :span="6">
                    <el-form-item label="背景" label-width="64px">
                      <el-select v-model="imageForm.background" style="width: 100%">
                        <el-option label="Auto" value="auto"></el-option>
                        <el-option label="不透明" value="opaque"></el-option>
                        <el-option label="透明" value="transparent"></el-option>
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-collapse-item>
            </el-collapse>
          </el-form-item>

          <div class="reference-panel">
            <div class="reference-head">
              <div>
                <span>参考图</span>
                <el-tag size="mini" type="info">{{ activeReferenceImages.length }}/{{ currentMaxReferenceImages }}</el-tag>
                <el-tag v-if="referenceImages.length > activeReferenceImages.length" size="mini" type="warning">仅使用前 {{ currentMaxReferenceImages }} 张</el-tag>
              </div>
              <div class="reference-actions">
                <el-upload
                    action="#"
                    multiple
                    accept="image/*"
                    :show-file-list="false"
                    :disabled="currentMaxReferenceImages === 0"
                    :before-upload="beforeReferenceUpload"
                    :http-request="uploadReferenceImage">
                  <el-button size="mini" icon="el-icon-upload2" :loading="uploadingReference" :disabled="currentMaxReferenceImages === 0">上传图片</el-button>
                </el-upload>
                <el-button size="mini" icon="el-icon-delete" :disabled="referenceImages.length === 0" @click="clearReferenceImages">清空</el-button>
              </div>
            </div>

            <div class="reference-url-row compact-input-row">
              <el-input
                  v-model.trim="referenceUrlInput"
                  size="mini"
                  clearable
                  placeholder="图片 URL"
                  @keyup.enter.native="addReferenceUrl">
              </el-input>
              <el-button size="mini" type="primary" icon="el-icon-plus" @click="addReferenceUrl">添加</el-button>
            </div>

            <div v-if="referenceImages.length" class="reference-grid">
              <div v-for="(item, index) in referenceImages" :key="item.url" class="reference-card" :class="{ disabled: !modeUsesReferences }">
                <div class="reference-index">{{ index + 1 }}</div>
                <img class="reference-thumb" :src="item.url" alt="参考图">
                <div class="reference-name" :title="item.name">{{ item.name }}</div>
                <div class="reference-card-actions">
                  <el-tooltip content="前移" placement="top">
                    <el-button type="text" size="mini" icon="el-icon-top" :disabled="index === 0" @click="moveReferenceImage(index, -1)"></el-button>
                  </el-tooltip>
                  <el-tooltip content="后移" placement="top">
                    <el-button type="text" size="mini" icon="el-icon-bottom" :disabled="index === referenceImages.length - 1" @click="moveReferenceImage(index, 1)"></el-button>
                  </el-tooltip>
                  <el-tooltip content="设为首图" placement="top">
                    <el-button type="text" size="mini" icon="el-icon-sort-up" :disabled="index === 0" @click="setReferenceAsFirst(index)"></el-button>
                  </el-tooltip>
                  <el-tooltip content="移除" placement="top">
                    <el-button type="text" size="mini" class="danger-link" icon="el-icon-close" @click="removeReferenceImage(index)"></el-button>
                  </el-tooltip>
                </div>
              </div>
            </div>
            <div v-else class="reference-empty">暂无参考图</div>
          </div>
        </el-form>
      </div>

      <div class="result-panel">
        <div class="panel-title">
          <span>本次结果</span>
          <div class="result-actions">
            <el-tag size="mini" type="info">{{ generatedImages.length }} 张</el-tag>
            <el-tag v-if="generatedImages.length" size="mini" type="warning">已选 {{ selectedGeneratedImages.length }}</el-tag>
            <el-button v-if="generatedImages.length" size="mini" icon="el-icon-plus" @click="addAllGeneratedToReference">全部加入参考</el-button>
            <el-button v-if="generatedImages.length" size="mini" icon="el-icon-document-copy" @click="copyAllGeneratedUrls">复制全部</el-button>
            <el-button v-if="generatedImages.length" size="mini" icon="el-icon-check" @click="selectAllGeneratedImages">全选</el-button>
            <el-button v-if="generatedImages.length" size="mini" :disabled="selectedGeneratedImages.length === 0" @click="clearGeneratedImageSelection">取消选择</el-button>
            <el-button
                v-if="generatedImages.length"
                size="mini"
                icon="el-icon-download"
                :loading="resultImageDownloadLoading"
                :disabled="selectedGeneratedImages.length === 0"
                @click="downloadSelectedGeneratedImages">
              下载选中
            </el-button>
          </div>
        </div>
        <div v-if="generatedImages.length" class="image-grid result-grid">
          <div
              v-for="image in generatedImages"
              :key="generatedImageKey(image)"
              class="image-card asset-card"
              :class="{ selected: isGeneratedImageSelected(image) }"
              @click="toggleGeneratedImageSelection(image, !isGeneratedImageSelected(image))">
            <el-checkbox class="asset-check" :value="isGeneratedImageSelected(image)" @click.native.stop @change="checked => toggleGeneratedImageSelection(image, checked)"></el-checkbox>
            <img class="image-thumb" :src="imageUrl(image)" alt="AI生成图片">
            <div class="image-meta" :title="imageName(image)">{{ imageName(image) }}</div>
            <div class="image-actions">
              <el-button type="text" size="mini" :disabled="!imageUrl(image)" @click.stop="openUrl(imageUrl(image))">打开</el-button>
              <el-button type="text" size="mini" :disabled="!imageUrl(image)" @click.stop="copyUrl(imageUrl(image))">复制</el-button>
              <el-button type="text" size="mini" :loading="resultImageDownloadLoading" :disabled="!imageUrl(image)" @click.stop="downloadGeneratedImage(image)">下载</el-button>
              <el-button type="text" size="mini" :disabled="!imageUrl(image)" @click.stop="addReferenceImage(imageUrl(image), imageName(image))">加入参考</el-button>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">暂无生成结果</div>
      </div>
    </section>

    <section class="panel batch-panel">
      <div class="panel-title">
        <span>批量生成</span>
        <div class="batch-summary">
          <el-tag size="mini" type="info">总数 {{ batchStats.total }}</el-tag>
          <el-tag size="mini" type="success">成功 {{ batchStats.succeeded }}</el-tag>
          <el-tag size="mini" type="danger">失败 {{ batchStats.failed }}</el-tag>
          <el-tag size="mini" type="warning">进行中 {{ batchStats.running }}</el-tag>
        </div>
      </div>

      <div class="batch-layout">
        <div class="batch-input">
          <div class="batch-controls">
            <span class="control-label">新增</span>
            <el-input-number
                v-model="batchAddCount"
                :min="1"
                :max="50"
                controls-position="right"
                size="mini">
            </el-input-number>
            <span class="control-unit">条</span>
            <el-button size="mini" icon="el-icon-plus" :disabled="batchRunning" @click="addBatchRows(batchAddCount)">添加行</el-button>
            <span class="control-label">并发</span>
            <el-input-number
                v-model="batchConcurrency"
                :min="1"
                :max="3"
                controls-position="right"
                size="mini">
            </el-input-number>
            <span class="control-label">间隔</span>
            <el-input-number
                v-model="batchLaunchInterval"
                :min="0"
                :max="10"
                controls-position="right"
                size="mini">
            </el-input-number>
            <span class="control-unit">秒</span>
            <el-button type="primary" size="mini" icon="el-icon-video-play" :loading="batchRunning" :disabled="batchRows.length === 0" @click="startBatchGenerate">开始生成</el-button>
            <el-button size="mini" icon="el-icon-switch-button" :disabled="!batchRunning" @click="stopBatchGenerate">停止排队</el-button>
            <el-button size="mini" icon="el-icon-refresh-left" :disabled="batchRunning || batchStats.failed === 0" @click="retryFailedBatch">重试失败</el-button>
            <el-button size="mini" icon="el-icon-delete" :disabled="batchRunning || batchRows.length === 0" @click="clearBatchRows">清空结果</el-button>
          </div>
        </div>

        <div class="batch-status-card">
          <div class="batch-status-title">当前配置</div>
          <div class="batch-config-tags">
            <el-tag size="mini" type="success">{{ currentModelLabel }}</el-tag>
            <el-tag size="mini" type="info">{{ currentModeConfig.label }}</el-tag>
            <el-tag size="mini" type="info">{{ referenceLimitText }}</el-tag>
            <el-tag size="mini" type="info">{{ outputPlanText }}</el-tag>
          </div>
          <el-progress :percentage="batchProgressPercent" :stroke-width="8" :show-text="false"></el-progress>
          <div class="batch-progress-text">{{ batchStats.done }}/{{ batchStats.total }} 完成</div>
        </div>
      </div>

      <el-table
          v-if="batchRows.length"
          :data="batchRows"
          size="mini"
          border
          stripe
          class="batch-table"
          max-height="420"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="index" label="序号" width="70"></el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="prompt" label="提示词" min-width="360">
          <template slot-scope="{ row }">
            <el-input
                v-model="row.prompt"
                type="textarea"
                :rows="2"
                :maxlength="currentPromptMaxLength"
                resize="none"
                show-word-limit
                :disabled="batchRunning || row.status === 'RUNNING'"
                placeholder="输入本条图片提示词"
                @input="markBatchRowDirty(row)">
            </el-input>
          </template>
        </el-table-column>
        <el-table-column label="参考图" min-width="260">
          <template slot-scope="{ row }">
            <div class="batch-reference-cell">
              <el-tag size="mini" :type="row.usesRowReferences ? 'warning' : 'info'">{{ row.usesRowReferences ? '本行' : '公共' }}</el-tag>
              <div v-if="batchRowReferenceUrls(row).length" class="task-image-strip compact-strip batch-reference-strip">
                <div
                    v-for="item in batchRowReferenceItems(row).slice(0, 3)"
                    :key="item.key"
                    class="batch-reference-thumb">
                  <img
                      :src="publicUrl(item.url)"
                      alt="批量参考图"
                      @click="openUrl(publicUrl(item.url))">
                  <button
                      v-if="item.own"
                      type="button"
                      class="batch-reference-delete"
                      title="删除参考图"
                      :disabled="batchRunning || row.status === 'RUNNING'"
                      @click.stop="removeBatchRowReference(row, item.index)">
                    <i class="el-icon-close"></i>
                  </button>
                </div>
                <span v-if="batchRowReferenceUrls(row).length > 3" class="task-image-more">+{{ batchRowReferenceUrls(row).length - 3 }}</span>
              </div>
              <span v-else class="muted-text">无</span>
              <div class="batch-reference-actions">
                <el-upload
                    action="#"
                    multiple
                    accept="image/*"
                    :show-file-list="false"
                    :disabled="currentMaxReferenceImages === 0"
                    :before-upload="beforeBatchReferenceUpload"
                    :http-request="request => uploadBatchRowReference(row, request)">
                  <el-button type="text" size="mini" :disabled="batchRunning || row.status === 'RUNNING' || currentMaxReferenceImages === 0">本地</el-button>
                </el-upload>
                <el-button type="text" size="mini" :disabled="batchRunning || row.status === 'RUNNING' || currentMaxReferenceImages === 0" @click="openBatchReferenceDialog(row)">图库</el-button>
                <el-button type="text" size="mini" :disabled="batchRunning || row.status === 'RUNNING' || !row.usesRowReferences" @click="clearBatchRowReferences(row)">用公共</el-button>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="结果" min-width="190">
          <template slot-scope="{ row }">
            <div v-if="batchRowUrls(row).length" class="task-image-strip">
              <img
                  v-for="url in batchRowUrls(row).slice(0, 3)"
                  :key="url"
                  :src="publicUrl(url)"
                  alt="批量生成结果"
                  @click="openUrl(publicUrl(url))">
              <span v-if="batchRowUrls(row).length > 3" class="task-image-more">+{{ batchRowUrls(row).length - 3 }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="错误信息" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.errorMessage || '-' }}</template>
        </el-table-column>
        <el-table-column label="耗时" width="86">
          <template slot-scope="{ row }">{{ batchElapsed(row) }}</template>
        </el-table-column>
        <el-table-column prop="taskNo" label="任务号" min-width="190" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.taskNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row, $index }">
            <el-button type="text" size="mini" :disabled="batchRunning || row.status === 'RUNNING'" @click="retryBatchRow(row)">重试</el-button>
            <el-button type="text" size="mini" :disabled="!batchRowUrls(row).length" @click="addBatchRowToReference(row)">加入参考</el-button>
            <el-button type="text" size="mini" :disabled="!batchRowUrls(row).length" @click="copyBatchRowUrls(row)">复制</el-button>
            <el-button type="text" size="mini" class="danger-link" :disabled="batchRunning" @click="removeBatchRow($index)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="empty-state batch-empty">暂无批量结果</div>
    </section>

    <section class="panel image-schedule-panel">
      <div class="panel-title">
        <span>图片定时任务</span>
        <el-form :inline="true" :model="scheduleTaskQuery" size="mini" class="query-form">
          <el-form-item>
            <el-tag size="mini" type="info">{{ imageTaskScopeLabel }}</el-tag>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="scheduleTaskQuery.status" clearable placeholder="全部" style="width: 130px">
              <el-option v-for="status in scheduleStatusOptions" :key="status.value" :label="status.label" :value="status.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="创建人" v-if="currentUserCanViewAllTasks()">
            <el-input v-model.trim="scheduleTaskQuery.createdBy" clearable placeholder="创建人" style="width: 130px"></el-input>
          </el-form-item>
          <el-form-item label="计划日期">
            <el-date-picker
                v-model="scheduleTaskQuery.dateRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 240px">
            </el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" :loading="scheduleTaskLoading" @click="searchScheduleTasks">查询</el-button>
            <el-button icon="el-icon-refresh" :loading="scheduleTaskLoading" @click="loadScheduleTasks">刷新</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table
          :data="scheduleTasks"
          size="mini"
          border
          stripe
          v-loading="scheduleTaskLoading"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="id" label="ID" width="72"></el-table-column>
        <el-table-column prop="createdBy" label="创建人" width="110">
          <template slot-scope="{ row }">{{ row.createdBy || '-' }}</template>
        </el-table-column>
        <el-table-column label="来源" width="110">
          <template slot-scope="{ row }">
            <el-tag v-if="isOwnImageTask(row)" size="mini" type="success">我的</el-tag>
            <el-tag v-else-if="row.sharedWithCurrentUser" size="mini" type="warning">{{ row.shareOwnerUsername || row.createdBy || '已分享' }}</el-tag>
            <el-tag v-else size="mini" type="info">{{ row.createdBy || '其他用户' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="model" label="模型" min-width="210" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="task-model-cell" :title="row.model">
              <span class="task-model-name">{{ imageModelDisplayName(row.model) }}</span>
              <span v-if="row.model" class="task-model-id">{{ row.model }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="mode" label="模式" width="110">
          <template slot-scope="{ row }">{{ modeLabel(row.mode) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="prompt" label="提示词" min-width="240" show-overflow-tooltip></el-table-column>
        <el-table-column label="结果" min-width="180">
          <template slot-scope="{ row }">
            <div v-if="imageTaskResultUrls(row).length" class="task-image-strip">
              <img
                  v-for="url in imageTaskResultUrls(row).slice(0, 3)"
                  :key="url"
                  :src="publicUrl(url)"
                  alt="定时生成结果"
                  @click="openUrl(publicUrl(url))">
              <span v-if="imageTaskResultUrls(row).length > 3" class="task-image-more">+{{ imageTaskResultUrls(row).length - 3 }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="scheduledAt" label="计划时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.scheduledAt) }}</template>
        </el-table-column>
        <el-table-column prop="startedAt" label="开始时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.startedAt) }}</template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="错误信息" min-width="190" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.errorMessage || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="190" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" :disabled="!row.prompt" @click="reuseScheduleTask(row)">复用参数</el-button>
            <el-button type="text" size="mini" :disabled="!imageTaskResultUrls(row).length" @click="addTaskResultsToReference(row)">结果入参</el-button>
            <el-button type="text" size="mini" class="danger-link" :disabled="!scheduleTaskCancellable(row)" @click="cancelScheduleTask(row)">取消</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ scheduleTaskPagination.total }} 条</span>
        <el-pagination
            background
            layout="sizes, prev, pager, next, jumper"
            :current-page="scheduleTaskPagination.page"
            :page-size="scheduleTaskPagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="scheduleTaskPagination.total"
            @size-change="handleScheduleTaskSizeChange"
            @current-change="handleScheduleTaskCurrentChange">
        </el-pagination>
      </div>
    </section>

    <section class="panel image-task-panel">
      <div class="panel-title">
        <span>图片生成记录</span>
        <el-form :inline="true" :model="imageTaskQuery" size="mini" class="query-form">
          <el-form-item>
            <el-tag size="mini" type="info">{{ imageTaskScopeLabel }}</el-tag>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="imageTaskQuery.status" clearable placeholder="全部" style="width: 130px">
              <el-option v-for="status in imageStatusOptions" :key="status.value" :label="status.label" :value="status.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="创建人" v-if="currentUserCanViewAllTasks()">
            <el-input v-model.trim="imageTaskQuery.createdBy" clearable placeholder="创建人" style="width: 130px"></el-input>
          </el-form-item>
          <el-form-item label="日期">
            <el-date-picker
                v-model="imageTaskQuery.dateRange"
                type="daterange"
                value-format="yyyy-MM-dd"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 240px">
            </el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" :loading="imageTaskLoading" @click="searchImageTasks">查询</el-button>
            <el-button icon="el-icon-refresh" :loading="imageTaskLoading" @click="loadImageTasks">刷新</el-button>
            <el-button
                icon="el-icon-download"
                :loading="imageTaskDownloadLoading"
                :disabled="selectedImageTaskRows.length === 0"
                @click="downloadSelectedImageTasks">
              下载选中{{ selectedImageTaskImageCount ? `(${selectedImageTaskImageCount})` : '' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table
          ref="imageTaskTable"
          :data="imageTasks"
          size="mini"
          border
          stripe
          v-loading="imageTaskLoading"
          @selection-change="handleImageTaskSelectionChange"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column type="selection" width="48" :selectable="imageTaskSelectable"></el-table-column>
        <el-table-column prop="id" label="ID" width="72"></el-table-column>
        <el-table-column prop="createdBy" label="创建人" width="110">
          <template slot-scope="{ row }">{{ row.createdBy || '-' }}</template>
        </el-table-column>
        <el-table-column label="来源" width="110">
          <template slot-scope="{ row }">
            <el-tag v-if="isOwnImageTask(row)" size="mini" type="success">我的</el-tag>
            <el-tag v-else-if="row.sharedWithCurrentUser" size="mini" type="warning">{{ row.shareOwnerUsername || row.createdBy || '已分享' }}</el-tag>
            <el-tag v-else size="mini" type="info">{{ row.createdBy || '其他用户' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="model" label="模型" min-width="210" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <div class="task-model-cell" :title="row.model">
              <span class="task-model-name">{{ imageModelDisplayName(row.model) }}</span>
              <span v-if="row.model" class="task-model-id">{{ row.model }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="mode" label="模式" width="110">
          <template slot-scope="{ row }">{{ modeLabel(row.mode) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="prompt" label="提示词" min-width="260" show-overflow-tooltip></el-table-column>
        <el-table-column label="结果" min-width="190">
          <template slot-scope="{ row }">
            <div v-if="imageTaskResultUrls(row).length" class="task-image-strip">
              <img
                  v-for="url in imageTaskResultUrls(row).slice(0, 3)"
                  :key="url"
                  :src="publicUrl(url)"
                  alt="生成结果"
                  @click="openUrl(publicUrl(url))">
              <span v-if="imageTaskResultUrls(row).length > 3" class="task-image-more">+{{ imageTaskResultUrls(row).length - 3 }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="imageCount" label="张数" width="76">
          <template slot-scope="{ row }">{{ row.imageCount || imageTaskResultUrls(row).length || '-' }}</template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="错误信息" min-width="190" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.errorMessage || '-' }}</template>
        </el-table-column>
        <el-table-column prop="recordCreatedAt" label="创建时间" width="170">
          <template slot-scope="{ row }">{{ formatTime(row.recordCreatedAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" :disabled="!row.prompt" @click="reuseImageTaskPrompt(row)">复用参数</el-button>
            <el-button type="text" size="mini" :disabled="!imageTaskResultUrls(row).length" @click="addTaskResultsToReference(row)">结果入参</el-button>
            <el-button type="text" size="mini" :disabled="!imageTaskResultUrls(row).length" @click="copyImageTaskUrls(row)">复制结果</el-button>
            <el-button type="text" size="mini" :loading="imageTaskDownloadLoading" :disabled="!imageTaskResultUrls(row).length" @click="downloadImageTaskImages(row)">下载</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ imageTaskPagination.total }} 条</span>
        <el-pagination
            background
            layout="sizes, prev, pager, next, jumper"
            :current-page="imageTaskPagination.page"
            :page-size="imageTaskPagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="imageTaskPagination.total"
            @size-change="handleImageTaskSizeChange"
            @current-change="handleImageTaskCurrentChange">
        </el-pagination>
      </div>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span>图片素材库</span>
        <div class="asset-actions">
          <el-input
              v-model.trim="assetKeyword"
              size="mini"
              clearable
              class="asset-search"
              prefix-icon="el-icon-search"
              placeholder="搜索文件名">
          </el-input>
          <el-tag size="mini" type="info">已选 {{ selectedImageAssetIds.length }}</el-tag>
          <el-button size="mini" :disabled="selectedImageAssetIds.length === 0" @click="clearAssetSelection">取消选择</el-button>
          <el-button size="mini" type="primary" icon="el-icon-plus" :disabled="selectedImageAssetIds.length === 0" @click="addSelectedAssetsToReference">加入选中参考图</el-button>
          <el-button size="mini" icon="el-icon-refresh" :loading="assetLoading" @click="loadImageAssets">刷新</el-button>
        </div>
      </div>
      <div v-if="filteredImageAssets.length" v-loading="assetLoading" class="image-grid asset-grid">
        <div
            v-for="asset in filteredImageAssets"
            :key="assetKey(asset)"
            class="image-card asset-card"
            :class="{ selected: isAssetSelected(asset) }"
            @click="toggleAssetSelection(asset, !isAssetSelected(asset))">
          <el-checkbox class="asset-check" :value="isAssetSelected(asset)" @click.native.stop @change="checked => toggleAssetSelection(asset, checked)"></el-checkbox>
          <img class="image-thumb" :src="publicUrl(asset.accessUrl)" alt="图片素材">
          <div class="image-meta" :title="asset.originalFileName || asset.fileName">{{ asset.originalFileName || asset.fileName || '-' }}</div>
          <div class="image-sub-meta">{{ formatTime(asset.recordCreatedAt) }}</div>
          <div class="image-actions">
            <el-button type="text" size="mini" :disabled="!asset.accessUrl" @click.stop="openUrl(publicUrl(asset.accessUrl))">打开</el-button>
            <el-button type="text" size="mini" :disabled="!asset.accessUrl" @click.stop="copyUrl(publicUrl(asset.accessUrl))">复制</el-button>
            <el-button type="text" size="mini" :disabled="!asset.accessUrl" @click.stop="addReferenceImage(publicUrl(asset.accessUrl), asset.originalFileName || asset.fileName)">加入参考</el-button>
          </div>
        </div>
      </div>
      <div v-else v-loading="assetLoading" class="empty-state">{{ assetKeyword ? '没有匹配的图片素材' : '暂无图片素材' }}</div>
    </section>

    <el-dialog
        title="选择本行参考图"
        :visible.sync="batchReferenceDialogVisible"
        width="920px"
        :close-on-click-modal="false">
      <div class="dialog-toolbar">
        <el-input
            v-model.trim="batchReferenceKeyword"
            size="mini"
            clearable
            class="asset-search"
            prefix-icon="el-icon-search"
            placeholder="搜索图片素材">
        </el-input>
        <el-tag size="mini" type="info">已选 {{ batchReferenceSelectedIds.length }}</el-tag>
        <el-button size="mini" icon="el-icon-refresh" :loading="assetLoading" @click="loadImageAssets">刷新</el-button>
      </div>
      <div v-if="filteredBatchReferenceAssets.length" v-loading="assetLoading" class="image-grid asset-grid dialog-asset-grid">
        <div
            v-for="asset in filteredBatchReferenceAssets"
            :key="assetKey(asset)"
            class="image-card asset-card"
            :class="{ selected: isBatchReferenceAssetSelected(asset) }"
            @click="toggleBatchReferenceAssetSelection(asset, !isBatchReferenceAssetSelected(asset))">
          <el-checkbox class="asset-check" :value="isBatchReferenceAssetSelected(asset)" @click.native.stop @change="checked => toggleBatchReferenceAssetSelection(asset, checked)"></el-checkbox>
          <img class="image-thumb" :src="publicUrl(asset.accessUrl)" alt="图片素材">
          <div class="image-meta" :title="asset.originalFileName || asset.fileName">{{ asset.originalFileName || asset.fileName || '-' }}</div>
          <div class="image-sub-meta">{{ formatTime(asset.recordCreatedAt) }}</div>
        </div>
      </div>
      <div v-else v-loading="assetLoading" class="empty-state">暂无可选图片素材</div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" :disabled="!activeBatchReferenceRow" @click="clearBatchRowReferences(activeBatchReferenceRow)">清空本行</el-button>
        <el-button size="mini" @click="batchReferenceDialogVisible = false">取消</el-button>
        <el-button size="mini" type="primary" @click="confirmBatchReferenceSelection">确认使用</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  cancelAigcImageSchedule,
  createAigcImageSchedule,
  downloadAigcImagesZip,
  generateAigcImage,
  listAigcImageSchedules,
  listAigcImageTasks,
  listAigcReferenceMedia,
  submitAigcImage,
  uploadAigcReferenceMedia
} from '@/api/aigcVideo'

export default {
  name: 'AigcImageGenerator',
  data() {
    return {
      generating: false,
      scheduling: false,
      assetLoading: false,
      imageTaskLoading: false,
      scheduleTaskLoading: false,
      imageTaskDownloadLoading: false,
      resultImageDownloadLoading: false,
      uploadingReference: false,
      referenceUploadCount: 0,
      referenceUrlInput: '',
      activeAdvancedPanels: [],
      assetKeyword: '',
      batchAddCount: 5,
      batchConcurrency: 2,
      batchLaunchInterval: 1,
      batchRunning: false,
      batchStopRequested: false,
      batchPromptLimit: 100,
      batchRows: [],
      batchReferenceDialogVisible: false,
      activeBatchReferenceRow: null,
      batchReferenceKeyword: '',
      batchReferenceSelectedIds: [],
      referenceImages: [],
      selectedImageAssetIds: [],
      imageTasks: [],
      selectedImageTaskRows: [],
      selectedGeneratedImageKeys: [],
      imageScheduleAt: '',
      scheduleTasks: [],
      scheduleTaskQuery: {
        status: '',
        createdBy: '',
        dateRange: []
      },
      scheduleTaskPagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      imageTaskQuery: {
        status: '',
        createdBy: '',
        dateRange: []
      },
      imageTaskPagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      imageForm: {
        provider: 'SEEDREAM',
        model: 'doubao-seedream-5-0-pro-260628',
        mode: 'TEXT_TO_IMAGE',
        prompt: '',
        size: '1024x1024',
        outputFormat: '',
        quality: 'auto',
        background: 'auto',
        maxImages: 1,
        seed: undefined,
        watermark: false,
        webSearch: false
      },
      imageRules: {
        provider: [{ required: true, message: '请选择服务商', trigger: 'change' }],
        model: [{ required: true, message: '请选择或输入图片模型', trigger: 'change' }],
        mode: [{ required: true, message: '请选择生成模式', trigger: 'change' }],
        prompt: [{ required: true, message: '请填写图片提示词', trigger: 'blur' }]
      },
      imageModelOptions: [
        { provider: 'SEEDREAM', value: 'doubao-seedream-5-0-pro-260628', label: 'Doubao-Seedream-5.0-pro - doubao-seedream-5-0-pro-260628' },
        { provider: 'SEEDREAM', value: 'doubao-seedream-5-0-260128', label: 'Doubao-Seedream-5.0-lite - doubao-seedream-5-0-260128' },
        { provider: 'SEEDREAM', value: 'doubao-seedream-4-5-251128', label: 'Doubao-Seedream-4.5 - doubao-seedream-4-5-251128' },
        { provider: 'SEEDREAM', value: 'doubao-seedream-4-0-250828', label: 'Doubao-Seedream-4.0 - doubao-seedream-4-0-250828' },
        { provider: 'GPT_IMAGE', value: 'gpt-image-2', label: 'GPT Image 2 - gpt-image-2' },
        { provider: 'GPT_IMAGE', value: 'gpt-image-2-2k', label: 'GPT Image 2 2K - gpt-image-2-2k' },
        { provider: 'GPT_IMAGE', value: 'gpt-image-2-4k', label: 'GPT Image 2 4K - gpt-image-2-4k' }
      ],
      imageSizeOptions: [
        { value: '1024x1024', label: '1:1 1024x1024' },
        { value: '864x1152', label: '3:4 864x1152' },
        { value: '1152x864', label: '4:3 1152x864' },
        { value: '720x1280', label: '9:16 720x1280' },
        { value: '1280x720', label: '16:9 1280x720' }
      ],
      imageStatusOptions: [
        { value: 'RUNNING', label: '生成中' },
        { value: 'SUCCEEDED', label: '成功' },
        { value: 'FAILED', label: '失败' }
      ],
      scheduleStatusOptions: [
        { value: 'CREATED', label: '待执行' },
        { value: 'RUNNING', label: '生成中' },
        { value: 'SUCCEEDED', label: '成功' },
        { value: 'FAILED', label: '失败' },
        { value: 'CANCELLED', label: '已取消' }
      ],
      modeCatalog: [
        { value: 'TEXT_TO_IMAGE', label: '文生图', requiresReference: false, minReferences: 0, maxReferences: 0 },
        { value: 'IMAGE_TO_IMAGE', label: '图生图', requiresReference: true, minReferences: 1, maxReferences: 1 },
        { value: 'FILE_IMAGE', label: '文件图', requiresReference: true, minReferences: 1, maxReferences: 14 },
        { value: 'MULTI_IMAGE_TO_IMAGE', label: '多图生图', requiresReference: true, minReferences: 2, maxReferences: 14 },
        { value: 'IMAGE_FUSION', label: '多图融合', requiresReference: true, minReferences: 2, maxReferences: 14 },
        { value: 'GROUP_IMAGE', label: '组图生成', requiresReference: false, minReferences: 0, maxReferences: 14, group: true },
        { value: 'LAYER_SPLIT', label: '图层拆分', requiresReference: true, minReferences: 1, maxReferences: 1, proOnly: true },
        { value: 'INTERACTIVE_EDIT', label: '交互编辑', requiresReference: true, minReferences: 1, maxReferences: 10, proOnly: true },
        { value: 'WEB_SEARCH', label: '联网搜索', requiresReference: false, minReferences: 0, maxReferences: 0, webSearch: true, liteOnly: true }
      ],
      generatedImages: [],
      imageAssets: []
    }
  },
  computed: {
    isGptImageProvider() {
      return this.imageForm.provider === 'GPT_IMAGE'
    },
    currentImageModelOptions() {
      return this.imageModelOptions.filter(item => !item.provider || item.provider === this.imageForm.provider)
    },
    currentPromptMaxLength() {
      return this.isGptImageProvider ? 8000 : 2000
    },
    currentModelCapability() {
      const model = String(this.imageForm.model || '').toLowerCase()
      if (this.isGptImageProvider || model.indexOf('gpt-image-2') >= 0) {
        return {
          type: 'gptImage',
          maxReferences: 0,
          maxGroupImages: 4,
          supportsGroup: true,
          supportsWebSearch: false,
          supportsLayerSplit: false,
          supportsInteractiveEdit: false,
          supportsOutputFormat: true,
          supportsQuality: true,
          supportsBackground: true
        }
      }
      if (model.indexOf('seedream-5-0-pro') >= 0) {
        return {
          type: 'pro',
          maxReferences: 10,
          maxGroupImages: 5,
          supportsGroup: false,
          supportsLoopMultiple: true,
          supportsWebSearch: false,
          supportsLayerSplit: true,
          supportsInteractiveEdit: true,
          supportsOutputFormat: true
        }
      }
      if (model.indexOf('seedream-5-0') >= 0) {
        return {
          type: 'lite',
          maxReferences: 14,
          maxGroupImages: 15,
          supportsGroup: true,
          supportsWebSearch: true,
          supportsLayerSplit: false,
          supportsInteractiveEdit: false,
          supportsOutputFormat: true
        }
      }
      if (model.indexOf('seedream-4-5') >= 0) {
        return {
          type: 'standard45',
          maxReferences: 14,
          maxGroupImages: 15,
          supportsGroup: true,
          supportsWebSearch: false,
          supportsLayerSplit: false,
          supportsInteractiveEdit: false,
          supportsOutputFormat: false
        }
      }
      if (model.indexOf('seedream-4-0') >= 0) {
        return {
          type: 'standard40',
          maxReferences: 14,
          maxGroupImages: 15,
          supportsGroup: true,
          supportsWebSearch: false,
          supportsLayerSplit: false,
          supportsInteractiveEdit: false,
          supportsOutputFormat: false
        }
      }
      return {
        type: 'standard',
        maxReferences: 14,
        maxGroupImages: 15,
        supportsGroup: true,
        supportsWebSearch: false,
        supportsLayerSplit: false,
        supportsInteractiveEdit: false,
          supportsOutputFormat: false
      }
    },
    currentImageSizeOptions() {
      const type = this.currentModelCapability.type
      const square2400 = { value: '2400x2400', label: '1:1 2400x2400' }
      if (type === 'gptImage') {
        return [
          { value: '1024x1024', label: '1K 正方形 1024x1024' },
          { value: '1536x864', label: '1K 横版 1536x864' },
          { value: '864x1536', label: '1K 竖版 864x1536' },
          { value: '2048x2048', label: '2K 正方形 2048x2048' },
          { value: '2560x1440', label: '2K 横版 2560x1440' },
          { value: '1440x2560', label: '2K 竖版 1440x2560' },
          { value: '3840x2160', label: '4K 横版 3840x2160' },
          { value: '2160x3840', label: '4K 竖版 2160x3840' }
        ]
      }
      if (type === 'pro') {
        return [
          { value: '1K', label: '1K 自动比例' },
          { value: '1.5K', label: '1.5K 自动比例' },
          { value: '2K', label: '2K 自动比例' },
          { value: '1024x1024', label: '1:1 1024x1024' },
          { value: '1536x1536', label: '1:1 1536x1536' },
          { value: '2048x2048', label: '1:1 2048x2048' },
          { value: '1280x720', label: '16:9 1280x720' },
          { value: '720x1280', label: '9:16 720x1280' }
        ]
      }
      if (type === 'lite') {
        return [
          { value: '2K', label: '2K 自动比例' },
          { value: '3K', label: '3K 自动比例' },
          { value: '4K', label: '4K 自动比例' },
          square2400,
          { value: '2048x2048', label: '1:1 2048x2048' },
          { value: '2560x1440', label: '16:9 2560x1440' },
          { value: '1440x2560', label: '9:16 1440x2560' }
        ]
      }
      if (type === 'standard45') {
        return [
          { value: '2K', label: '2K 自动比例' },
          { value: '4K', label: '4K 自动比例' },
          square2400,
          { value: '2048x2048', label: '1:1 2048x2048' },
          { value: '2560x1440', label: '16:9 2560x1440' },
          { value: '1440x2560', label: '9:16 1440x2560' }
        ]
      }
      return [
        { value: '1K', label: '1K 自动比例' },
        { value: '2K', label: '2K 自动比例' },
        { value: '4K', label: '4K 自动比例' },
        square2400,
        { value: '1024x1024', label: '1:1 1024x1024' },
        { value: '2048x2048', label: '1:1 2048x2048' },
        { value: '1280x720', label: '16:9 1280x720' },
        { value: '720x1280', label: '9:16 720x1280' }
      ]
    },
    currentModelLabel() {
      const option = this.imageModelOptions.find(item => item.value === this.imageForm.model)
      if (option) {
        return option.label.split(' - ')[0]
      }
      return this.imageForm.model || '未选择模型'
    },
    currentModeOptions() {
      if (this.isGptImageProvider) {
        return this.modeCatalog.filter(mode => mode.value === 'TEXT_TO_IMAGE')
      }
      const capability = this.currentModelCapability
      return this.modeCatalog.filter(mode => {
        if (mode.proOnly) {
          return capability.type === 'pro'
        }
        if (mode.liteOnly) {
          return capability.type === 'lite'
        }
        if (mode.group) {
          return capability.supportsGroup
        }
        return true
      })
    },
    currentModeConfig() {
      return this.modeCatalog.find(mode => mode.value === this.imageForm.mode) || this.modeCatalog[0]
    },
    modeUsesReferences() {
      return this.currentModeConfig.requiresReference || this.currentModeConfig.maxReferences > 0
    },
    currentMaxReferenceImages() {
      if (!this.modeUsesReferences) {
        return 0
      }
      return Math.min(this.currentModeConfig.maxReferences || this.currentModelCapability.maxReferences, this.currentModelCapability.maxReferences)
    },
    activeReferenceImages() {
      if (!this.modeUsesReferences) {
        return []
      }
      return this.referenceImages.slice(0, this.currentMaxReferenceImages)
    },
    currentMaxOutputImages() {
      if (!this.currentAllowsMultipleOutputs) {
        return 1
      }
      if (this.currentModelCapability.supportsLoopMultiple) {
        return Math.max(1, Math.min(this.currentModelCapability.maxGroupImages, 5))
      }
      const remaining = this.activeReferenceImages.length > 0 ? 15 - this.activeReferenceImages.length : 15
      return Math.max(1, Math.min(this.currentModelCapability.maxGroupImages, remaining))
    },
    currentAllowsMultipleOutputs() {
      return (this.isGptImageProvider || this.currentModelCapability.supportsGroup || this.currentModelCapability.supportsLoopMultiple) && this.currentModeConfig.value !== 'LAYER_SPLIT'
    },
    referenceLimitText() {
      if (!this.modeUsesReferences) {
        return '无需参考图'
      }
      const min = this.currentModeConfig.minReferences || 0
      return `${this.activeReferenceImages.length}/${this.currentMaxReferenceImages} 参考图${min ? `，至少 ${min} 张` : ''}`
    },
    outputPlanText() {
      if (this.currentAllowsMultipleOutputs) {
        if (this.currentModelCapability.supportsLoopMultiple) {
          return `循环生成 ${this.imageForm.maxImages}/${this.currentMaxOutputImages} 张`
        }
        return `输出 ${this.imageForm.maxImages}/${this.currentMaxOutputImages} 张`
      }
      return '单张输出'
    },
    currentPromptPlaceholder() {
      const placeholders = {
        TEXT_TO_IMAGE: '描述要生成的图片内容、主体、构图、色彩、风格等',
        IMAGE_TO_IMAGE: '描述基于参考图要调整的内容、风格或画面变化',
        FILE_IMAGE: '描述基于上传图片要生成的画面',
        MULTI_IMAGE_TO_IMAGE: '描述多张参考图之间的主体、风格或构图关系',
        IMAGE_FUSION: '描述需要融合的元素、保留对象和最终画面',
        GROUP_IMAGE: '描述一组图片的统一主题、变化方向和数量关系',
        LAYER_SPLIT: '描述需要拆分的主体、背景和图层范围',
        INTERACTIVE_EDIT: '描述编辑位置和改动内容，例如坐标、框选区域或箭头指向',
        WEB_SEARCH: '描述需要结合联网信息生成的图片主题'
      }
      return placeholders[this.imageForm.mode] || placeholders.TEXT_TO_IMAGE
    },
    filteredImageAssets() {
      const keyword = String(this.assetKeyword || '').trim().toLowerCase()
      if (!keyword) {
        return this.imageAssets
      }
      return this.imageAssets.filter(asset => {
        const text = [
          asset.originalFileName,
          asset.fileName,
          asset.accessUrl,
          asset.assetNo
        ].filter(Boolean).join(' ').toLowerCase()
        return text.indexOf(keyword) >= 0
      })
    },
    batchPrompts() {
      return this.batchRows
          .map(row => String(row.prompt || '').trim())
          .filter(Boolean)
          .slice(0, this.batchPromptLimit)
    },
    filteredBatchReferenceAssets() {
      const keyword = String(this.batchReferenceKeyword || '').trim().toLowerCase()
      if (!keyword) {
        return this.imageAssets
      }
      return this.imageAssets.filter(asset => {
        const text = [
          asset.originalFileName,
          asset.fileName,
          asset.accessUrl,
          asset.assetNo
        ].filter(Boolean).join(' ').toLowerCase()
        return text.indexOf(keyword) >= 0
      })
    },
    batchStats() {
      const stats = {
        total: this.batchRows.length,
        waiting: 0,
        running: 0,
        succeeded: 0,
        failed: 0,
        cancelled: 0,
        done: 0
      }
      this.batchRows.forEach(row => {
        if (row.status === 'WAITING') stats.waiting += 1
        if (row.status === 'RUNNING') stats.running += 1
        if (row.status === 'SUCCEEDED') stats.succeeded += 1
        if (row.status === 'FAILED') stats.failed += 1
        if (row.status === 'CANCELLED') stats.cancelled += 1
      })
      stats.done = stats.succeeded + stats.failed + stats.cancelled
      return stats
    },
    batchProgressPercent() {
      if (!this.batchStats.total) {
        return 0
      }
      return Math.round((this.batchStats.done / this.batchStats.total) * 100)
    },
    selectedImageTaskImageCount() {
      return (this.selectedImageTaskRows || []).reduce((total, row) => total + this.imageTaskResultUrls(row).length, 0)
    },
    selectedGeneratedImages() {
      return (this.generatedImages || []).filter(image => this.selectedGeneratedImageKeys.includes(this.generatedImageKey(image)))
    },
    imageTaskScopeLabel() {
      return this.currentUserCanViewAllTasks() ? '可看全部记录' : '我的和已分享'
    }
  },
  async mounted() {
    await this.ensureCurrentUserProfile()
    this.loadImageAssets()
    this.loadScheduleTasks()
    this.loadImageTasks()
  },
  methods: {
    async ensureCurrentUserProfile() {
      if (this.currentUserCanViewAllTasks()) {
        return
      }
      const username = this.currentUsername()
      if (!username || username === 'unknown') {
        return
      }
      try {
        const response = await this.$axios.get('/permission/users', {
          baseURL: 'https://dy.darknight.games/api'
        })
        const payload = response && response.data ? response.data : response
        const users = Array.isArray(payload.data) ? payload.data : []
        const currentUser = users.find(user => String(user.username || '') === username)
        if (currentUser) {
          this.saveCurrentUserProfile(currentUser)
        }
      } catch (e) {
        // 权限信息兜底失败时，继续按当前缓存身份查询任务。
      }
    },
    saveCurrentUserProfile(profile) {
      if (!profile) {
        return
      }
      const localUser = this.localStoredUser()
      const accountType = this.firstDefined(
          profile.accountType,
          profile.account_type,
          profile.userType,
          profile.user_type,
          profile.type
      )
      const nextUser = Object.assign({}, localUser, profile, {
        username: this.firstDefined(profile.username, localUser.username, this.currentUsername())
      })
      if (accountType !== undefined) {
        nextUser.accountType = accountType
        nextUser.account_type = accountType
      }
      const userId = this.firstDefined(profile.id, profile.userId, profile.user_id, localUser.id, localUser.userId)
      if (userId !== undefined) {
        nextUser.id = userId
        nextUser.userId = userId
      }
      localStorage.setItem('user', JSON.stringify(nextUser))
      if (this.$store && this.$store.commit) {
        this.$store.commit('login', nextUser)
      }
    },
    selectMode(value) {
      if (this.imageForm.mode === value) {
        return
      }
      this.imageForm.mode = value
      this.handleModeChange()
    },
    modeMeta(mode) {
      if (mode.group) {
        const remaining = this.referenceImages.length > 0 ? 15 - this.referenceImages.length : 15
        const maxImages = Math.max(1, Math.min(this.currentModelCapability.maxGroupImages, remaining))
        return `组图，最多 ${maxImages} 张`
      }
      if (mode.webSearch) {
        return '联网生成'
      }
      if (!mode.requiresReference) {
        return this.isGptImageProvider ? '仅文生图' : '不需要参考图'
      }
      if (mode.minReferences === mode.maxReferences) {
        return `${mode.minReferences} 张参考图`
      }
      return `${mode.minReferences || 0}-${Math.min(mode.maxReferences || this.currentModelCapability.maxReferences, this.currentModelCapability.maxReferences)} 张参考图`
    },
    handleProviderChange() {
      const options = this.currentImageModelOptions
      if (!this.imageForm.model || !options.some(item => item.value === this.imageForm.model)) {
        this.imageForm.model = options.length ? options[0].value : ''
      }
      if (this.isGptImageProvider) {
        this.imageForm.mode = 'TEXT_TO_IMAGE'
        this.imageForm.outputFormat = this.imageForm.outputFormat || 'png'
        this.imageForm.quality = this.imageForm.quality || 'auto'
        this.imageForm.background = this.imageForm.background || 'auto'
        this.imageForm.watermark = false
        this.imageForm.webSearch = false
      }
      this.syncImageSizeForCurrentModel()
      this.handleModeChange()
    },
    handleModelChange() {
      if (!this.currentModeOptions.some(mode => mode.value === this.imageForm.mode)) {
        this.imageForm.mode = this.currentModeOptions[0].value
      }
      this.syncImageSizeForCurrentModel()
      this.handleModeChange()
    },
    syncImageSizeForCurrentModel() {
      const options = this.currentImageSizeOptions || []
      if (options.length && !options.some(option => option.value === this.imageForm.size)) {
        this.imageForm.size = options[0].value
      }
    },
    handleModeChange() {
      if (!this.currentModelCapability.supportsOutputFormat) {
        this.imageForm.outputFormat = ''
      } else if (this.isGptImageProvider && !this.imageForm.outputFormat) {
        this.imageForm.outputFormat = 'png'
      } else if (!this.isGptImageProvider && this.imageForm.outputFormat === 'webp') {
        this.imageForm.outputFormat = ''
      }
      if (this.isGptImageProvider) {
        this.imageForm.quality = this.imageForm.quality || 'auto'
        this.imageForm.background = this.imageForm.background || 'auto'
        this.imageForm.watermark = false
      }
      this.imageForm.webSearch = this.isGptImageProvider ? false : this.currentModeConfig.webSearch === true
      if (!this.currentAllowsMultipleOutputs) {
        this.imageForm.maxImages = 1
      } else if (this.imageForm.maxImages > this.currentMaxOutputImages) {
        this.imageForm.maxImages = this.currentMaxOutputImages
      } else if (!this.imageForm.maxImages || this.imageForm.maxImages < 1) {
        this.imageForm.maxImages = 1
      }
    },
    generateImage() {
      this.$refs.imageForm.validate(async valid => {
        if (!valid || !this.validateBeforeGenerate()) return
        this.generating = true
        try {
          const response = await submitAigcImage(this.buildImageGeneratePayload(String(this.imageForm.prompt || '').trim()))
          const data = response.data || {}
          this.generatedImages = []
          this.selectedGeneratedImageKeys = []
          this.$message.success(data.taskNo ? `已提交生成任务：${data.taskNo}` : '已提交生成任务')
          this.loadImageTasks({ silent: true })
        } catch (error) {
          this.$message.error(error.message || '提交图片生成失败')
          this.loadImageTasks({ silent: true })
        } finally {
          this.generating = false
        }
      })
    },
    scheduleImageGeneration() {
      this.$refs.imageForm.validate(async valid => {
        if (!valid || !this.validateBeforeGenerate()) return
        if (!this.imageScheduleAt) {
          this.$message.warning('请选择预约时间')
          return
        }
        if (!this.isFutureScheduleTime(this.imageScheduleAt)) {
          this.$message.warning('预约时间不能早于当前时间')
          return
        }
        this.scheduling = true
        try {
          const prompt = String(this.imageForm.prompt || '').trim()
          await createAigcImageSchedule({
            scheduledAt: this.imageScheduleAt,
            generateRequest: this.buildImageGeneratePayload(prompt)
          })
          this.$message.success('定时生成任务已创建')
          this.loadScheduleTasks({ silent: true })
        } catch (error) {
          this.$message.error(error.message || '创建定时任务失败')
        } finally {
          this.scheduling = false
        }
      })
    },
    isFutureScheduleTime(value) {
      const text = String(value || '').trim()
      if (!text) {
        return false
      }
      const date = new Date(text.replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) {
        return true
      }
      return date.getTime() >= Date.now() - 60000
    },
    buildImageGeneratePayload(prompt) {
      return this.buildImageGeneratePayloadFromSnapshot(prompt, this.currentImageGenerateSnapshot())
    },
    currentImageGenerateSnapshot() {
      return {
        provider: this.imageForm.provider,
        model: this.imageForm.model,
        mode: this.imageForm.mode,
        images: this.activeReferenceImages.map(item => item.url),
        size: this.imageForm.size,
        outputFormat: this.currentModelCapability.supportsOutputFormat ? this.imageForm.outputFormat : '',
        quality: this.isGptImageProvider ? this.imageForm.quality : '',
        background: this.isGptImageProvider ? this.imageForm.background : '',
        watermark: this.imageForm.watermark,
        webSearch: this.imageForm.webSearch,
        seed: this.imageForm.seed,
        maxImages: this.currentAllowsMultipleOutputs ? this.imageForm.maxImages : 1,
        sequentialImageGeneration: this.currentModelCapability.supportsGroup && this.imageForm.maxImages > 1 ? 'auto' : 'disabled'
      }
    },
    buildImageGeneratePayloadFromSnapshot(prompt, snapshot) {
      const config = snapshot || this.currentImageGenerateSnapshot()
      return {
        provider: config.provider,
        model: config.model,
        mode: config.mode,
        prompt: String(prompt || '').trim(),
        images: Array.isArray(config.images) ? config.images : [],
        size: config.size,
        responseFormat: 'url',
        outputFormat: config.outputFormat,
        quality: config.quality,
        background: config.background,
        watermark: config.watermark,
        webSearch: config.webSearch,
        seed: config.seed,
        maxImages: config.maxImages,
        sequentialImageGeneration: config.sequentialImageGeneration
      }
    },
    validateBeforeGenerate() {
      const config = this.currentModeConfig
      const count = this.activeReferenceImages.length
      if (this.isGptImageProvider && count > 0) {
        this.$message.warning('GPT Image 2 当前只支持文生图，不支持参考图')
        return false
      }
      if (config.minReferences && count < config.minReferences) {
        this.$message.warning(`当前模式至少需要 ${config.minReferences} 张参考图`)
        return false
      }
      if (config.maxReferences !== undefined && count > this.currentMaxReferenceImages) {
        this.$message.warning(`当前模式最多支持 ${this.currentMaxReferenceImages} 张参考图`)
        return false
      }
      if (this.currentModelCapability.supportsGroup && count > 0 && count + this.imageForm.maxImages > 15) {
        this.$message.warning('参考图数量 + 输出张数不能超过 15')
        return false
      }
      return true
    },
    validateBeforeBatchGenerate() {
      if (this.batchRows.length === 0) {
        this.$message.warning('请先添加批量任务行')
        return false
      }
      const runnableRows = this.batchRows.filter(row => row.status === 'WAITING' && String(row.prompt || '').trim())
      if (runnableRows.length === 0) {
        this.$message.warning('没有待生成的有效提示词')
        return false
      }
      if (!this.imageForm.model) {
        this.$message.warning('请选择图片模型')
        return false
      }
      for (const row of runnableRows) {
        const error = this.validateBatchRowConfig(row, this.batchRowGenerateSnapshot(row))
        if (error) {
          this.$message.warning(`第 ${row.index} 行：${error}`)
          return false
        }
      }
      return true
    },
    createEmptyBatchRow() {
      return {
        id: `batch-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        index: this.batchRows.length + 1,
        prompt: '',
        status: 'WAITING',
        referenceImages: [],
        referenceAssetIds: [],
        usesRowReferences: false,
        images: [],
        errorMessage: '',
        taskNo: '',
        taskId: null,
        elapsedMs: 0,
        startedAt: null,
        config: null
      }
    },
    addBatchRows(count) {
      const addCount = Math.min(Math.max(Number(count) || 1, 1), 50)
      const remaining = Math.max(this.batchPromptLimit - this.batchRows.length, 0)
      if (remaining <= 0) {
        this.$message.warning(`单次最多保留 ${this.batchPromptLimit} 条`)
        return
      }
      const finalCount = Math.min(addCount, remaining)
      for (let i = 0; i < finalCount; i += 1) {
        this.batchRows.push(this.createEmptyBatchRow())
      }
      this.updateBatchRowIndexes()
    },
    async startBatchGenerate() {
      if (this.batchRunning || !this.validateBeforeBatchGenerate()) {
        return
      }
      const runnableRows = this.batchRows.filter(row => row.status === 'WAITING' && String(row.prompt || '').trim())
      await this.runBatchQueue(runnableRows)
    },
    batchRowGenerateSnapshot(row) {
      const base = this.currentImageGenerateSnapshot()
      const ownReferences = this.batchRowOwnReferenceUrls(row)
      const images = base.provider === 'GPT_IMAGE' ? [] : (ownReferences.length > 0 ? ownReferences : base.images)
      return Object.assign({}, base, {
        images,
        mode: this.resolveBatchRowMode(base.mode, images.length)
      })
    },
    resolveBatchRowMode(currentMode, referenceCount) {
      if (referenceCount <= 0) {
        return currentMode
      }
      const current = this.modeCatalog.find(mode => mode.value === currentMode)
      if (current && current.group) {
        return currentMode
      }
      const maxReferences = current ? (current.maxReferences === undefined ? this.currentModelCapability.maxReferences : current.maxReferences) : 0
      if (current && current.requiresReference && referenceCount >= (current.minReferences || 0) && referenceCount <= maxReferences) {
        return currentMode
      }
      const preferredMode = referenceCount > 1 ? 'MULTI_IMAGE_TO_IMAGE' : 'IMAGE_TO_IMAGE'
      return this.currentModeOptions.some(mode => mode.value === preferredMode) ? preferredMode : currentMode
    },
    validateBatchRowConfig(row, snapshot) {
      if (!String(row.prompt || '').trim()) {
        return '提示词不能为空'
      }
      const config = this.modeCatalog.find(mode => mode.value === snapshot.mode) || this.currentModeConfig
      const count = Array.isArray(snapshot.images) ? snapshot.images.length : 0
      const maxReferences = this.batchModeMaxReferences(config)
      if (snapshot.provider === 'GPT_IMAGE' && count > 0) {
        return 'GPT Image 2 当前只支持文生图，不支持参考图'
      }
      if (config.minReferences && count < config.minReferences) {
        return `当前模式至少需要 ${config.minReferences} 张参考图`
      }
      if (config.maxReferences !== undefined && count > maxReferences) {
        return `当前模式最多支持 ${maxReferences} 张参考图`
      }
      if (this.currentModelCapability.supportsGroup && (snapshot.maxImages || 1) > 1 && count > 0 && count + (snapshot.maxImages || 1) > 15) {
        return '参考图数量 + 输出张数不能超过 15'
      }
      return ''
    },
    batchModeMaxReferences(config) {
      if (!config) {
        return this.currentModelCapability.maxReferences
      }
      return Math.min(config.maxReferences || this.currentModelCapability.maxReferences, this.currentModelCapability.maxReferences)
    },
    async retryFailedBatch() {
      if (this.batchRunning) {
        return
      }
      const rows = this.batchRows.filter(row => row.status === 'FAILED' || row.status === 'CANCELLED')
      if (!rows.length) {
        this.$message.warning('没有可重试的失败项')
        return
      }
      rows.forEach(row => this.resetBatchRow(row))
      await this.runBatchQueue(rows)
    },
    async retryBatchRow(row) {
      if (this.batchRunning || !row) {
        return
      }
      this.resetBatchRow(row)
      await this.runBatchQueue([row])
    },
    async runBatchQueue(rows) {
      const runnableRows = (rows || []).filter(row => row.status === 'WAITING')
      if (!runnableRows.length) {
        return
      }
      this.batchRunning = true
      this.batchStopRequested = false
      let cursor = 0
      const workerCount = Math.min(Math.max(Number(this.batchConcurrency) || 1, 1), 3, runnableRows.length)
      const worker = async () => {
        while (cursor < runnableRows.length && !this.batchStopRequested) {
          const row = runnableRows[cursor]
          cursor += 1
          await this.runBatchRow(row)
          if (!this.batchStopRequested && this.batchLaunchInterval > 0 && cursor < runnableRows.length) {
            await this.sleep(Number(this.batchLaunchInterval) * 1000)
          }
        }
      }
      try {
        await Promise.all(Array.from({ length: workerCount }, () => worker()))
      } finally {
        if (this.batchStopRequested) {
          runnableRows.forEach(row => {
            if (row.status === 'WAITING') {
              row.status = 'CANCELLED'
              row.errorMessage = '已停止排队'
            }
          })
        }
        this.batchRunning = false
        this.batchStopRequested = false
        this.loadImageAssets()
        this.loadImageTasks({ silent: true })
      }
    },
    async runBatchRow(row) {
      row.status = 'RUNNING'
      row.startedAt = Date.now()
      row.elapsedMs = 0
      row.errorMessage = ''
      row.images = []
      row.taskNo = ''
      row.taskId = null
      try {
        const snapshot = this.batchRowGenerateSnapshot(row)
        row.config = snapshot
        const error = this.validateBatchRowConfig(row, snapshot)
        if (error) {
          throw new Error(error)
        }
        const response = await generateAigcImage(this.buildImageGeneratePayloadFromSnapshot(row.prompt, snapshot))
        const data = response.data || {}
        row.images = data.images || []
        row.taskNo = data.taskNo || ''
        row.taskId = data.taskId || null
        if (row.images.length > 0) {
          row.status = 'SUCCEEDED'
          this.generatedImages = row.images
          this.selectedGeneratedImageKeys = []
        } else {
          row.status = 'FAILED'
          row.errorMessage = '没有返回可用图片'
        }
      } catch (error) {
        row.status = 'FAILED'
        row.errorMessage = error.message || '图片生成失败'
      } finally {
        row.elapsedMs = Date.now() - row.startedAt
      }
    },
    resetBatchRow(row) {
      row.status = 'WAITING'
      row.images = []
      row.errorMessage = ''
      row.taskNo = ''
      row.taskId = null
      row.elapsedMs = 0
      row.startedAt = null
      row.config = null
    },
    stopBatchGenerate() {
      this.batchStopRequested = true
      this.$message.warning('已停止继续排队，生成中的请求会正常完成')
    },
    clearBatchRows() {
      this.batchRows = []
      this.activeBatchReferenceRow = null
    },
    removeBatchRow(index) {
      this.batchRows.splice(index, 1)
      this.updateBatchRowIndexes()
    },
    updateBatchRowIndexes() {
      this.batchRows.forEach((row, index) => {
        row.index = index + 1
      })
    },
    markBatchRowDirty(row) {
      if (!row || row.status === 'WAITING' || row.status === 'RUNNING') {
        return
      }
      row.status = 'WAITING'
      row.images = []
      row.errorMessage = ''
      row.taskNo = ''
      row.taskId = null
      row.elapsedMs = 0
      row.startedAt = null
      row.config = null
    },
    sleep(ms) {
      return new Promise(resolve => window.setTimeout(resolve, Math.max(Number(ms) || 0, 0)))
    },
    batchRowOwnReferenceItems(row) {
      if (!row || !Array.isArray(row.referenceImages)) {
        return []
      }
      return row.referenceImages
          .map((item, index) => {
            const url = this.publicUrl(item.url || item.accessUrl || item)
            if (!url) {
              return null
            }
            return {
              url,
              index,
              own: true,
              key: `row-${row.id || row.index}-${index}-${url}`
            }
          })
          .filter(Boolean)
    },
    batchRowOwnReferenceUrls(row) {
      return this.batchRowOwnReferenceItems(row).map(item => item.url)
    },
    batchRowReferenceItems(row) {
      const ownItems = this.batchRowOwnReferenceItems(row)
      if (ownItems.length > 0) {
        return ownItems
      }
      if (row && row.config && Array.isArray(row.config.images) && row.config.images.length > 0) {
        return row.config.images
            .map((url, index) => {
              const normalizedUrl = this.publicUrl(url)
              return normalizedUrl ? {
                url: normalizedUrl,
                index,
                own: false,
                key: `config-${row.id || row.index}-${index}-${normalizedUrl}`
              } : null
            })
            .filter(Boolean)
      }
      return this.activeReferenceImages
          .map((item, index) => {
            const url = this.publicUrl(item.url)
            return url ? {
              url,
              index,
              own: false,
              key: `public-${index}-${url}`
            } : null
          })
          .filter(Boolean)
    },
    batchRowReferenceUrls(row) {
      return this.batchRowReferenceItems(row).map(item => item.url)
    },
    clearBatchRowReferences(row) {
      if (!row) {
        return
      }
      row.referenceImages = []
      row.referenceAssetIds = []
      row.usesRowReferences = false
      row.config = null
      this.markBatchRowDirty(row)
      if (this.activeBatchReferenceRow && this.activeBatchReferenceRow.id === row.id) {
        this.batchReferenceSelectedIds = []
      }
    },
    removeBatchRowReference(row, index) {
      if (!row || row.status === 'RUNNING' || !Array.isArray(row.referenceImages)) {
        return
      }
      row.referenceImages.splice(index, 1)
      const assetIds = row.referenceImages
          .map(item => item && item.assetId)
          .filter(Boolean)
          .map(id => String(id))
      row.referenceAssetIds = Array.from(new Set(assetIds))
      row.usesRowReferences = row.referenceImages.length > 0
      row.config = null
      this.markBatchRowDirty(row)
      if (this.activeBatchReferenceRow && this.activeBatchReferenceRow.id === row.id) {
        this.batchReferenceSelectedIds = row.referenceAssetIds.slice()
      }
    },
    beforeBatchReferenceUpload(file) {
      const type = file && file.type ? file.type : ''
      const name = file && file.name ? file.name.toLowerCase() : ''
      const isImage = type.indexOf('image/') === 0 || /\.(jpg|jpeg|png|webp|gif)$/.test(name)
      if (!isImage) {
        this.$message.warning('请选择图片文件')
        return false
      }
      return true
    },
    async uploadBatchRowReference(row, request) {
      if (!row || !request || !request.file) {
        return
      }
      this.uploadingReference = true
      try {
        const response = await uploadAigcReferenceMedia(request.file, { mediaType: 'image' })
        const media = response.data || {}
        this.addBatchRowReference(row, media.accessUrl || '', media.originalFileName || request.file.name || '上传图片', media.id)
        if (request.onSuccess) {
          request.onSuccess(media)
        }
        this.$message.success('本行参考图已上传')
        this.loadImageAssets()
      } catch (error) {
        if (request.onError) {
          request.onError(error)
        }
        this.$message.error(error.message || '参考图上传失败')
      } finally {
        this.uploadingReference = false
      }
    },
    addBatchRowReference(row, url, name, assetId) {
      const normalizedUrl = this.publicUrl(url)
      if (!row || !normalizedUrl) {
        return false
      }
      if (!Array.isArray(row.referenceImages)) {
        this.$set(row, 'referenceImages', [])
      }
      if (this.batchRowOwnReferenceUrls(row).length >= this.currentModelCapability.maxReferences) {
        this.$message.warning(`当前模型最多支持 ${this.currentModelCapability.maxReferences} 张参考图`)
        return false
      }
      if (row.referenceImages.some(item => this.publicUrl(item.url) === normalizedUrl)) {
        this.$message.warning('本行参考图已存在')
        return false
      }
      row.referenceImages.push({
        url: normalizedUrl,
        name: name || this.mediaNameFromUrl(normalizedUrl) || '参考图',
        assetId: assetId || null
      })
      if (assetId) {
        if (!Array.isArray(row.referenceAssetIds)) {
          this.$set(row, 'referenceAssetIds', [])
        }
        const id = String(assetId)
        if (!row.referenceAssetIds.includes(id)) {
          row.referenceAssetIds.push(id)
        }
      }
      row.usesRowReferences = true
      row.config = null
      this.markBatchRowDirty(row)
      return true
    },
    openBatchReferenceDialog(row) {
      this.activeBatchReferenceRow = row
      this.batchReferenceSelectedIds = Array.isArray(row.referenceAssetIds) ? row.referenceAssetIds.slice() : []
      this.batchReferenceKeyword = ''
      this.batchReferenceDialogVisible = true
      if (!this.imageAssets.length) {
        this.loadImageAssets()
      }
    },
    isBatchReferenceAssetSelected(asset) {
      return this.batchReferenceSelectedIds.includes(this.assetKey(asset))
    },
    toggleBatchReferenceAssetSelection(asset, checked) {
      const key = this.assetKey(asset)
      if (!key) {
        return
      }
      if (checked && !this.batchReferenceSelectedIds.includes(key)) {
        if (this.batchReferenceSelectedIds.length >= this.currentModelCapability.maxReferences) {
          this.$message.warning(`当前模型最多支持 ${this.currentModelCapability.maxReferences} 张参考图`)
          return
        }
        this.batchReferenceSelectedIds.push(key)
      } else if (!checked) {
        this.batchReferenceSelectedIds = this.batchReferenceSelectedIds.filter(item => item !== key)
      }
    },
    confirmBatchReferenceSelection() {
      const row = this.activeBatchReferenceRow
      if (!row) {
        this.batchReferenceDialogVisible = false
        return
      }
      const selected = this.imageAssets.filter(asset => this.batchReferenceSelectedIds.includes(this.assetKey(asset)))
      row.referenceImages = selected
          .filter(asset => asset.accessUrl)
          .map(asset => ({
            url: this.publicUrl(asset.accessUrl),
            name: asset.originalFileName || asset.fileName || '素材图',
            assetId: asset.id || asset.assetNo || null
          }))
      row.referenceAssetIds = selected.map(asset => this.assetKey(asset)).filter(Boolean)
      row.usesRowReferences = row.referenceImages.length > 0
      row.config = null
      this.markBatchRowDirty(row)
      this.batchReferenceDialogVisible = false
    },
    batchRowUrls(row) {
      if (!row || !Array.isArray(row.images)) {
        return []
      }
      return row.images.map(image => this.imageUrl(image)).filter(Boolean)
    },
    addBatchRowToReference(row) {
      let added = 0
      this.batchRowUrls(row).forEach((url, index) => {
        if (this.addReferenceImage(url, `批量结果图 ${row.index || ''}-${index + 1}`)) {
          added += 1
        }
      })
      if (added > 0) {
        this.$message.success(`已加入 ${added} 张参考图`)
      }
    },
    async copyBatchRowUrls(row) {
      const urls = this.batchRowUrls(row)
      if (!urls.length) {
        return
      }
      try {
        await navigator.clipboard.writeText(urls.join('\n'))
        this.$message.success('结果图片地址已复制')
      } catch (e) {
        this.$message.warning('复制失败，请逐张复制')
      }
    },
    batchElapsed(row) {
      if (!row) {
        return '-'
      }
      if (row.status === 'RUNNING') {
        return '生成中'
      }
      if (!row.elapsedMs) {
        return '-'
      }
      return `${(row.elapsedMs / 1000).toFixed(1)}s`
    },
    async loadImageAssets() {
      this.assetLoading = true
      try {
        const response = await listAigcReferenceMedia({
          mediaType: 'image',
          page: 0,
          size: 80
        })
        const pageData = response.data || {}
        this.imageAssets = pageData.content || []
        this.selectedImageAssetIds = this.selectedImageAssetIds.filter(id =>
          this.imageAssets.some(asset => this.assetKey(asset) === id)
        )
      } catch (error) {
        this.imageAssets = []
        this.$message.error(error.message || '获取图片素材失败')
      } finally {
        this.assetLoading = false
      }
    },
    async loadScheduleTasks(options = {}) {
      const silent = !!options.silent
      this.scheduleTaskLoading = true
      try {
        const dateRange = this.scheduleTaskQuery.dateRange || []
        const response = await listAigcImageSchedules({
          status: this.scheduleTaskQuery.status,
          createdBy: this.scheduleTaskQuery.createdBy,
          startDate: dateRange[0],
          endDate: dateRange[1],
          page: this.scheduleTaskPagination.page - 1,
          size: this.scheduleTaskPagination.pageSize
        })
        const pageData = response.data || {}
        this.scheduleTasks = pageData.content || []
        this.scheduleTaskPagination.total = Number(pageData.totalElements || 0)
      } catch (error) {
        if (!silent) {
          this.$message.error(error.message || '获取图片定时任务失败')
        }
      } finally {
        this.scheduleTaskLoading = false
      }
    },
    searchScheduleTasks() {
      this.scheduleTaskPagination.page = 1
      this.loadScheduleTasks()
    },
    handleScheduleTaskSizeChange(size) {
      this.scheduleTaskPagination.pageSize = size
      this.scheduleTaskPagination.page = 1
      this.loadScheduleTasks()
    },
    handleScheduleTaskCurrentChange(page) {
      this.scheduleTaskPagination.page = page
      this.loadScheduleTasks()
    },
    scheduleTaskCancellable(row) {
      return row && row.status === 'CREATED' && (this.currentUserCanViewAllTasks() || this.isOwnImageTask(row))
    },
    async cancelScheduleTask(row) {
      if (!row || !row.id) {
        return
      }
      try {
        await this.$confirm('确认取消这个定时生成任务？', '取消确认', {
          confirmButtonText: '取消任务',
          cancelButtonText: '保留',
          type: 'warning'
        })
        await cancelAigcImageSchedule(row.id)
        this.$message.success('定时任务已取消')
        this.loadScheduleTasks({ silent: true })
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || '取消定时任务失败')
        }
      }
    },
    reuseScheduleTask(row) {
      this.reuseImageTaskPrompt(row)
      if (row && row.scheduledAt) {
        this.imageScheduleAt = ''
      }
    },
    async loadImageTasks(options = {}) {
      const silent = !!options.silent
      this.imageTaskLoading = true
      try {
        const dateRange = this.imageTaskQuery.dateRange || []
        const response = await listAigcImageTasks({
          provider: '',
          status: this.imageTaskQuery.status,
          createdBy: this.imageTaskQuery.createdBy,
          startDate: dateRange[0],
          endDate: dateRange[1],
          page: this.imageTaskPagination.page - 1,
          size: this.imageTaskPagination.pageSize
        })
        const pageData = response.data || {}
        this.imageTasks = pageData.content || []
        this.imageTaskPagination.total = Number(pageData.totalElements || 0)
        this.selectedImageTaskRows = []
        this.$nextTick(() => {
          if (this.$refs.imageTaskTable && this.$refs.imageTaskTable.clearSelection) {
            this.$refs.imageTaskTable.clearSelection()
          }
        })
      } catch (error) {
        if (!silent) {
          this.$message.error(error.message || '获取图片生成记录失败')
        }
      } finally {
        this.imageTaskLoading = false
      }
    },
    imageTaskSelectable(row) {
      return this.imageTaskResultUrls(row).length > 0
    },
    handleImageTaskSelectionChange(rows) {
      this.selectedImageTaskRows = (rows || []).filter(row => this.imageTaskResultUrls(row).length > 0)
    },
    async downloadSelectedImageTasks() {
      const items = this.imageDownloadItemsFromTasks(this.selectedImageTaskRows)
      if (!items.length) {
        this.$message.warning('选中的任务没有可下载图片')
        return
      }
      await this.downloadImageItems(items, 'imageTaskDownloadLoading')
    },
    async downloadImageTaskImages(row) {
      const items = this.imageDownloadItemsFromTasks([row])
      if (!items.length) {
        this.$message.warning('当前任务没有可下载图片')
        return
      }
      await this.downloadImageItems(items, 'imageTaskDownloadLoading')
    },
    imageDownloadItemsFromTasks(rows) {
      const items = []
      ;(rows || []).forEach((row, rowIndex) => {
        this.imageTaskResultUrls(row).forEach((url, imageIndex) => {
          const fallback = `image_task_${row && row.id ? row.id : rowIndex + 1}_${imageIndex + 1}`
          items.push({
            url: this.publicUrl(url),
            fileName: this.buildImageDownloadFileName(url, fallback)
          })
        })
      })
      return items
    },
    searchImageTasks() {
      this.imageTaskPagination.page = 1
      this.loadImageTasks()
    },
    handleImageTaskSizeChange(size) {
      this.imageTaskPagination.pageSize = size
      this.imageTaskPagination.page = 1
      this.loadImageTasks()
    },
    handleImageTaskCurrentChange(page) {
      this.imageTaskPagination.page = page
      this.loadImageTasks()
    },
    beforeReferenceUpload(file) {
      const type = file && file.type ? file.type : ''
      const name = file && file.name ? file.name.toLowerCase() : ''
      const isImage = type.indexOf('image/') === 0 || /\.(jpg|jpeg|png|webp|gif)$/.test(name)
      if (!isImage) {
        this.$message.warning('请选择图片文件')
        return false
      }
      if (this.modeUsesReferences && this.referenceImages.length >= this.currentMaxReferenceImages) {
        this.$message.warning(`当前模式最多支持 ${this.currentMaxReferenceImages} 张参考图`)
        return false
      }
      return true
    },
    async uploadReferenceImage(request) {
      const file = request.file
      this.referenceUploadCount += 1
      this.uploadingReference = true
      try {
        const response = await uploadAigcReferenceMedia(file, { mediaType: 'image' })
        const media = response.data || {}
        this.addReferenceImage(media.accessUrl || '', media.originalFileName || file.name || '上传图片')
        if (request.onSuccess) {
          request.onSuccess(media)
        }
        this.$message.success('参考图已上传')
        this.loadImageAssets()
      } catch (error) {
        if (request.onError) {
          request.onError(error)
        }
        this.$message.error(error.message || '参考图上传失败')
      } finally {
        this.referenceUploadCount = Math.max(0, this.referenceUploadCount - 1)
        this.uploadingReference = this.referenceUploadCount > 0
      }
    },
    addReferenceUrl() {
      const url = String(this.referenceUrlInput || '').trim()
      if (!url) {
        this.$message.warning('请输入图片 URL')
        return
      }
      if (this.addReferenceImage(url, this.mediaNameFromUrl(url))) {
        this.referenceUrlInput = ''
      }
    },
    addReferenceImage(url, name) {
      const normalizedUrl = this.publicUrl(url)
      if (!normalizedUrl) {
        this.$message.warning('图片地址不可用')
        return false
      }
      if (this.referenceImages.some(item => item.url === normalizedUrl)) {
        this.$message.warning('参考图已存在')
        return false
      }
      this.ensureReferenceModeForCount(this.referenceImages.length + 1)
      if (this.modeUsesReferences && this.referenceImages.length >= this.currentMaxReferenceImages) {
        this.$message.warning(`当前模式最多支持 ${this.currentMaxReferenceImages} 张参考图`)
        return false
      }
      this.referenceImages.push({
        url: normalizedUrl,
        name: name || this.mediaNameFromUrl(normalizedUrl) || '参考图'
      })
      if (this.imageForm.maxImages > this.currentMaxOutputImages) {
        this.imageForm.maxImages = this.currentMaxOutputImages
      }
      return true
    },
    ensureReferenceModeForCount(nextCount) {
      const currentMode = this.imageForm.mode
      if (currentMode === 'TEXT_TO_IMAGE' || currentMode === 'WEB_SEARCH' || (currentMode === 'IMAGE_TO_IMAGE' && nextCount > 1)) {
        const preferredMode = nextCount > 1 ? 'MULTI_IMAGE_TO_IMAGE' : 'IMAGE_TO_IMAGE'
        if (this.currentModeOptions.some(mode => mode.value === preferredMode)) {
          this.imageForm.mode = preferredMode
          this.handleModeChange()
        }
      }
    },
    removeReferenceImage(index) {
      this.referenceImages.splice(index, 1)
      if (this.imageForm.maxImages > this.currentMaxOutputImages) {
        this.imageForm.maxImages = this.currentMaxOutputImages
      }
    },
    moveReferenceImage(index, direction) {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= this.referenceImages.length) {
        return
      }
      const list = this.referenceImages.slice()
      const current = list[index]
      this.$set(list, index, list[nextIndex])
      this.$set(list, nextIndex, current)
      this.referenceImages = list
    },
    setReferenceAsFirst(index) {
      if (index <= 0 || index >= this.referenceImages.length) {
        return
      }
      const list = this.referenceImages.slice()
      const current = list.splice(index, 1)[0]
      list.unshift(current)
      this.referenceImages = list
    },
    clearReferenceImages() {
      this.referenceImages = []
      if (this.imageForm.maxImages > this.currentMaxOutputImages) {
        this.imageForm.maxImages = this.currentMaxOutputImages
      }
    },
    addSelectedAssetsToReference() {
      const selected = this.imageAssets.filter(asset => this.selectedImageAssetIds.includes(this.assetKey(asset)))
      let added = 0
      selected.forEach(asset => {
        if (this.addReferenceImage(this.publicUrl(asset.accessUrl), asset.originalFileName || asset.fileName)) {
          added += 1
        }
      })
      if (added > 0) {
        this.$message.success(`已加入 ${added} 张参考图`)
      }
    },
    addAllGeneratedToReference() {
      let added = 0
      this.generatedImages.forEach(image => {
        const url = this.imageUrl(image)
        if (this.addReferenceImage(url, this.imageName(image))) {
          added += 1
        }
      })
      if (added > 0) {
        this.$message.success(`已加入 ${added} 张参考图`)
      }
    },
    generatedImageKey(image) {
      return `${image && image.index !== undefined ? image.index : 'image'}-${this.imageUrl(image)}`
    },
    isGeneratedImageSelected(image) {
      return this.selectedGeneratedImageKeys.includes(this.generatedImageKey(image))
    },
    toggleGeneratedImageSelection(image, checked) {
      const key = this.generatedImageKey(image)
      if (!key || !this.imageUrl(image)) {
        return
      }
      if (checked && !this.selectedGeneratedImageKeys.includes(key)) {
        this.selectedGeneratedImageKeys.push(key)
      } else if (!checked) {
        this.selectedGeneratedImageKeys = this.selectedGeneratedImageKeys.filter(item => item !== key)
      }
    },
    selectAllGeneratedImages() {
      this.selectedGeneratedImageKeys = (this.generatedImages || [])
          .filter(image => this.imageUrl(image))
          .map(image => this.generatedImageKey(image))
    },
    clearGeneratedImageSelection() {
      this.selectedGeneratedImageKeys = []
    },
    async downloadSelectedGeneratedImages() {
      const items = this.imageDownloadItemsFromGenerated(this.selectedGeneratedImages)
      if (!items.length) {
        this.$message.warning('请选择要下载的图片')
        return
      }
      await this.downloadImageItems(items, 'resultImageDownloadLoading')
    },
    async downloadGeneratedImage(image) {
      const items = this.imageDownloadItemsFromGenerated([image])
      if (!items.length) {
        this.$message.warning('当前图片不可下载')
        return
      }
      await this.downloadImageItems(items, 'resultImageDownloadLoading')
    },
    imageDownloadItemsFromGenerated(images) {
      return (images || [])
          .map((image, index) => {
            const url = this.imageUrl(image)
            if (!url) {
              return null
            }
            const fallback = `generated_image_${image && image.index ? image.index : index + 1}`
            return {
              url,
              fileName: this.buildImageDownloadFileName(url, this.imageName(image) || fallback)
            }
          })
          .filter(Boolean)
    },
    async copyAllGeneratedUrls() {
      const urls = this.generatedImages.map(image => this.imageUrl(image)).filter(Boolean)
      if (urls.length === 0) {
        return
      }
      try {
        await navigator.clipboard.writeText(urls.join('\n'))
        this.$message.success('全部图片地址已复制')
      } catch (e) {
        this.$message.warning('复制失败，请逐张复制')
      }
    },
    clearAssetSelection() {
      this.selectedImageAssetIds = []
    },
    reuseImageTaskPrompt(row) {
      if (!row) {
        return
      }
      this.imageForm.provider = row.provider || 'SEEDREAM'
      this.handleProviderChange()
      this.imageForm.model = row.model || this.imageForm.model
      this.handleModelChange()
      if (row.mode && this.currentModeOptions.some(mode => mode.value === row.mode)) {
        this.imageForm.mode = row.mode
      }
      this.imageForm.prompt = row.prompt || ''
      if (row.size && this.currentImageSizeOptions.some(option => option.value === row.size)) {
        this.imageForm.size = row.size
      } else {
        this.syncImageSizeForCurrentModel()
      }
      this.imageForm.outputFormat = this.currentModelCapability.supportsOutputFormat ? (row.outputFormat || (this.isGptImageProvider ? 'png' : '')) : ''
      this.imageForm.quality = row.quality || 'auto'
      this.imageForm.background = row.background || 'auto'
      this.imageForm.seed = row.seed === null || row.seed === undefined ? undefined : row.seed
      this.imageForm.watermark = !!row.watermark
      this.imageForm.webSearch = !!row.webSearch
      const references = this.imageTaskReferenceImages(row)
      this.referenceImages = references.slice(0, this.currentModelCapability.maxReferences).map((url, index) => ({
        url: this.publicUrl(url),
        name: `历史参考图 ${index + 1}`
      }))
      this.handleModeChange()
      this.$message.success('已复用历史参数')
    },
    addTaskResultsToReference(row) {
      let added = 0
      this.imageTaskResultUrls(row).forEach((url, index) => {
        if (this.addReferenceImage(url, `历史结果图 ${index + 1}`)) {
          added += 1
        }
      })
      if (added > 0) {
        this.$message.success(`已加入 ${added} 张参考图`)
      }
    },
    async copyImageTaskUrls(row) {
      const urls = this.imageTaskResultUrls(row)
      if (!urls.length) {
        return
      }
      try {
        await navigator.clipboard.writeText(urls.map(url => this.publicUrl(url)).join('\n'))
        this.$message.success('结果图片地址已复制')
      } catch (e) {
        this.$message.warning('复制失败，请逐张复制')
      }
    },
    async downloadImageItems(items, loadingKey) {
      const downloadableItems = (items || []).filter(item => item && item.url)
      if (!downloadableItems.length) {
        this.$message.warning('没有可下载图片')
        return
      }
      this[loadingKey] = true
      let successCount = 0
      let failedCount = 0
      try {
        const remoteItems = downloadableItems.filter(item => this.isRemoteHttpUrl(item.url))
        const directItems = downloadableItems.filter(item => !this.isRemoteHttpUrl(item.url))
        if (remoteItems.length > 1) {
          try {
            await this.downloadImageZip(remoteItems)
            successCount += remoteItems.length
          } catch (error) {
            failedCount += remoteItems.length
            this.$message.error(error.message || '图片打包下载失败')
          }
        }
        const singleItems = remoteItems.length === 1 ? remoteItems.concat(directItems) : directItems
        for (let i = 0; i < singleItems.length; i += 1) {
          try {
            await this.downloadImageItem(singleItems[i])
            successCount += 1
          } catch (error) {
            failedCount += 1
            console.error('下载图片失败', singleItems[i], error)
          }
          await this.sleep(180)
        }
        if (failedCount > 0) {
          this.$message.warning(`已处理 ${successCount} 张，失败 ${failedCount} 张`)
        } else if (remoteItems.length > 1) {
          this.$message.success(`已打包下载 ${successCount} 张图片`)
        } else {
          this.$message.success(`已触发下载 ${successCount} 张图片`)
        }
      } finally {
        this[loadingKey] = false
      }
    },
    async downloadImageZip(items) {
      const zipName = this.buildImageZipFileName()
      try {
        const blob = await downloadAigcImagesZip({
          zipName,
          images: items.map(item => ({
            url: this.publicUrl(item.url),
            fileName: this.buildImageDownloadFileName(item.url, item.fileName)
          }))
        })
        if (!(blob instanceof Blob)) {
          throw new Error('下载包响应格式错误')
        }
        if (blob.type && blob.type.indexOf('text/plain') >= 0) {
          const text = await blob.text()
          throw new Error(text || '图片打包下载失败')
        }
        const blobUrl = URL.createObjectURL(blob)
        this.triggerImageDownload(blobUrl, zipName)
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 3000)
      } catch (error) {
        throw new Error(await this.resolveDownloadErrorMessage(error))
      }
    },
    async downloadImageItem(item) {
      const url = this.publicUrl(item.url)
      const fileName = this.buildImageDownloadFileName(url, item.fileName)
      if (!url) {
        throw new Error('缺少图片下载地址')
      }
      if (url.startsWith('data:') || url.startsWith('blob:')) {
        this.triggerImageDownload(url, fileName)
        return
      }
      try {
        const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        this.triggerImageDownload(blobUrl, this.buildImageDownloadFileName(url, fileName, blob.type))
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
      } catch (error) {
        this.triggerImageDownload(this.buildImageDownloadUrl(url), fileName)
      }
    },
    isRemoteHttpUrl(url) {
      const value = this.publicUrl(url)
      return /^https?:\/\//i.test(value)
    },
    buildImageZipFileName() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      return `aigc_images_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.zip`
    },
    async resolveDownloadErrorMessage(error) {
      const data = error && error.response && error.response.data
      if (data instanceof Blob) {
        try {
          return await data.text()
        } catch (e) {
          return error.message || '图片打包下载失败'
        }
      }
      return error && error.message ? error.message : '图片打包下载失败'
    },
    buildImageDownloadUrl(url) {
      const value = this.publicUrl(url)
      if (!value || value.startsWith('data:') || value.startsWith('blob:') || /[?&]download=/i.test(value)) {
        return value
      }
      const separator = value.includes('?') ? '&' : '?'
      return `${value}${separator}download=true`
    },
    triggerImageDownload(url, fileName) {
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || 'image.png'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    buildImageDownloadFileName(url, rawName, mimeType) {
      const fallback = this.mediaNameFromUrl(url) || 'image'
      const baseName = String(rawName || fallback || 'image')
          .replace(/[\\/:*?"<>|]+/g, '_')
          .replace(/\s+/g, ' ')
          .trim() || 'image'
      if (/\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(baseName)) {
        return baseName
      }
      const extension = this.imageExtensionFromUrl(url) || this.imageExtensionFromMime(mimeType) || 'png'
      return `${baseName}.${extension}`
    },
    imageExtensionFromUrl(url) {
      const cleanUrl = String(url || '').split('?')[0].split('#')[0]
      const match = cleanUrl.match(/\.([a-zA-Z0-9]{2,5})$/)
      if (!match) {
        return ''
      }
      const extension = match[1].toLowerCase()
      return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tif', 'tiff'].includes(extension) ? extension : ''
    },
    imageExtensionFromMime(mimeType) {
      const type = String(mimeType || '').toLowerCase()
      if (type.includes('png')) return 'png'
      if (type.includes('jpeg') || type.includes('jpg')) return 'jpg'
      if (type.includes('webp')) return 'webp'
      if (type.includes('gif')) return 'gif'
      if (type.includes('bmp')) return 'bmp'
      return ''
    },
    imageTaskResultUrls(row) {
      if (!row) {
        return []
      }
      const urls = []
      if (Array.isArray(row.resultUrls)) {
        row.resultUrls.forEach(url => {
          if (url) urls.push(url)
        })
      }
      if (row.resultUrl) {
        urls.push(row.resultUrl)
      }
      return Array.from(new Set(urls.filter(Boolean)))
    },
    imageTaskReferenceImages(row) {
      if (!row || !Array.isArray(row.referenceImages)) {
        return []
      }
      return row.referenceImages.filter(Boolean)
    },
    isOwnImageTask(row) {
      if (!row) {
        return false
      }
      if (row.ownedByCurrentUser === true) {
        return true
      }
      const userId = this.currentUserId()
      const username = this.currentUsername()
      return (!!userId && row.creatorUserId === userId) || (!!username && row.createdBy === username)
    },
    imageModelDisplayName(value) {
      const option = this.imageModelOptions.find(item => item.value === value)
      if (option) {
        return option.label.split(' - ')[0]
      }
      return value || '-'
    },
    modeLabel(value) {
      const mode = this.modeCatalog.find(item => item.value === value)
      return mode ? mode.label : (value || '-')
    },
    statusLabel(value) {
      const map = {
        CREATED: '待执行',
        SUBMITTED: '已提交',
        WAITING: '待生成',
        QUEUED: '排队中',
        RUNNING: '生成中',
        SUCCEEDED: '成功',
        FAILED: '失败',
        CANCELLED: '已取消',
        EXPIRED: '已过期'
      }
      return map[value] || value || '-'
    },
    statusType(value) {
      const map = {
        SUCCEEDED: 'success',
        FAILED: 'danger',
        CREATED: 'info',
        WAITING: 'info',
        RUNNING: 'warning',
        QUEUED: 'warning',
        SUBMITTED: 'info',
        CANCELLED: 'info',
        EXPIRED: 'info'
      }
      return map[value] || 'info'
    },
    currentUserId() {
      const storeUser = this.$store && this.$store.state ? (this.$store.state.user || {}) : {}
      const localUser = this.localStoredUser()
      const value = this.firstDefined(storeUser.id, storeUser.userId, localUser.id, localUser.userId)
      return value !== undefined && value !== null ? String(value) : ''
    },
    currentUsername() {
      const storeUser = this.$store && this.$store.state ? (this.$store.state.user || {}) : {}
      const localUser = this.localStoredUser()
      return String(this.firstDefined(storeUser.username, localUser.username, '') || '')
    },
    currentAccountType() {
      const storeUser = this.$store && this.$store.state ? (this.$store.state.user || {}) : {}
      const localUser = this.localStoredUser()
      const value = this.firstDefined(
          storeUser.accountType,
          storeUser.account_type,
          storeUser.userType,
          storeUser.user_type,
          storeUser.type,
          localUser.accountType,
          localUser.account_type,
          localUser.userType,
          localUser.user_type,
          localUser.type
      )
      return value !== undefined && value !== null ? String(value) : ''
    },
    currentUserCanViewAllTasks() {
      const storeUser = this.$store && this.$store.state ? (this.$store.state.user || {}) : {}
      const localUser = this.localStoredUser()
      const accountType = this.currentAccountType()
      const role = String(this.firstDefined(storeUser.role, storeUser.userRole, localUser.role, localUser.userRole, '')).toLowerCase()
      const username = String(this.currentUsername() || '').toLowerCase()
      return accountType === '1'
          || this.truthy(storeUser.admin)
          || this.truthy(storeUser.isAdmin)
          || this.truthy(localUser.admin)
          || this.truthy(localUser.isAdmin)
          || role === 'admin'
          || role === 'administrator'
          || username === 'admin'
          || username === 'administrator'
    },
    localStoredUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}') || {}
      } catch (e) {
        return {}
      }
    },
    firstDefined() {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i]
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
      return undefined
    },
    truthy(value) {
      return value === true || value === 1 || String(value).toLowerCase() === 'true'
    },
    assetKey(asset) {
      return String(asset.id || asset.assetNo || asset.accessUrl || '')
    },
    isAssetSelected(asset) {
      return this.selectedImageAssetIds.includes(this.assetKey(asset))
    },
    toggleAssetSelection(asset, checked) {
      const key = this.assetKey(asset)
      if (!key) {
        return
      }
      if (checked && !this.selectedImageAssetIds.includes(key)) {
        this.selectedImageAssetIds.push(key)
      } else if (!checked) {
        this.selectedImageAssetIds = this.selectedImageAssetIds.filter(item => item !== key)
      }
    },
    imageUrl(image) {
      if (!image) {
        return ''
      }
      const url = image.accessUrl || (image.asset && image.asset.accessUrl) || image.providerUrl || ''
      return this.publicUrl(url)
    },
    imageName(image) {
      const asset = image && image.asset ? image.asset : {}
      return asset.originalFileName || asset.fileName || `生成图片 ${image && image.index ? image.index : ''}`
    },
    publicUrl(url) {
      const value = String(url || '').trim()
      if (!value) {
        return ''
      }
      if (/^(https?:)?\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
        return value
      }
      if (value.startsWith('/')) {
        return value
      }
      return 'https://' + value
    },
    mediaNameFromUrl(url) {
      const value = String(url || '').trim()
      if (!value) {
        return ''
      }
      const path = value.split('?')[0].replace(/\\/g, '/')
      return path.substring(path.lastIndexOf('/') + 1) || value
    },
    openUrl(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    async copyUrl(url) {
      if (!url) {
        return
      }
      try {
        await navigator.clipboard.writeText(url)
        this.$message.success('图片地址已复制')
      } catch (e) {
        this.$message.warning('复制失败，请打开后手动复制')
      }
    },
    formatTime(value) {
      if (!value) return '-'
      const text = String(value)
      if (/^\d+$/.test(text)) {
        const timestamp = Number(text)
        if (timestamp > 0) {
          const date = new Date(timestamp)
          if (!Number.isNaN(date.getTime())) {
            const pad = number => String(number).padStart(2, '0')
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
          }
        }
      }
      return text.replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.aigc-image-page {
  min-height: 100%;
  padding: 18px 20px 24px;
  background: #f4f6fa;
  color: #1f2937;
  box-sizing: border-box;
}

.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.page-head h2 {
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0;
}

.page-head p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.head-actions,
.asset-actions,
.create-actions,
.result-actions,
.reference-actions,
.reference-head > div {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.panel {
  margin-bottom: 14px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  box-sizing: border-box;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
}

.image-workspace {
  display: grid;
  grid-template-columns: minmax(540px, 0.78fr) minmax(520px, 1fr);
  gap: 16px;
  align-items: start;
  justify-content: stretch;
}

.form-panel,
.result-panel {
  min-width: 0;
}

.form-panel {
  max-width: 960px;
}

.panel-title,
.reference-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: #111827;
  font-size: 15px;
  font-weight: 700;
}

.query-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px 8px;
}

.query-form .el-form-item {
  margin-right: 0;
  margin-bottom: 0;
}

.batch-layout {
  display: grid;
  grid-template-columns: minmax(520px, 1fr) 320px;
  gap: 14px;
  align-items: stretch;
}

.batch-input {
  min-width: 0;
  display: flex;
  align-items: center;
}

.batch-summary,
.batch-controls,
.batch-config-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.batch-controls {
  margin-top: 10px;
}

.control-label,
.control-unit {
  color: #6b7280;
  font-size: 12px;
}

.batch-status-card {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  background: #fbfdff;
}

.batch-status-title {
  margin-bottom: 10px;
  color: #111827;
  font-size: 14px;
  font-weight: 700;
}

.batch-config-tags {
  margin-bottom: 18px;
}

.batch-progress-text {
  margin-top: 8px;
  color: #6b7280;
  font-size: 12px;
}

.batch-table {
  margin-top: 14px;
}

.batch-empty {
  min-height: 116px;
  margin-top: 12px;
}

.batch-reference-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}

.batch-reference-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.batch-reference-actions .el-button {
  margin: 0;
}

.compact-strip img {
  width: 40px;
  height: 40px;
}

.batch-reference-strip {
  gap: 8px;
}

.batch-reference-thumb {
  position: relative;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
}

.batch-reference-thumb img {
  width: 40px;
  height: 40px;
}

.batch-reference-delete {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid #ffffff;
  border-radius: 50%;
  color: #ffffff;
  background: #f56c6c;
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.18);
}

.batch-reference-delete:hover {
  background: #e34d4d;
}

.batch-reference-delete:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.batch-reference-delete i {
  font-size: 12px;
}

.muted-text {
  color: #9ca3af;
  font-size: 12px;
}

.dialog-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.dialog-asset-grid {
  max-height: 520px;
  overflow: auto;
}

.image-form {
  min-width: 0;
}

.capability-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: -2px 0 14px 92px;
  padding: 9px 10px;
  border: 1px solid #e8edf5;
  border-radius: 5px;
  background: #fbfdff;
}

.capability-name {
  margin-right: 4px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
}

.mode-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px;
}

.mode-card {
  min-height: 62px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.mode-card:hover {
  background: #ffffff;
  border-color: #a9c7f8;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.08);
  transform: translateY(-1px);
}

.mode-card.active {
  background: #eef5ff;
  border-color: #409eff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.22);
}

.mode-title {
  display: block;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.mode-meta {
  display: block;
  margin-top: 5px;
  color: #6b7280;
  font-size: 12px;
  line-height: 16px;
}

.advanced-collapse {
  border: 1px solid #e8edf5;
  border-radius: 5px;
  overflow: hidden;
}

.advanced-title {
  color: #4b5563;
  font-size: 13px;
}

.switch-stack {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-right: 10px;
  color: #4b5563;
  font-size: 12px;
}

.reference-panel {
  margin-left: 92px;
  padding: 12px;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  background: #fbfdff;
}

.reference-url-row {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 78px;
  gap: 8px;
  margin-bottom: 10px;
}

.compact-input-row {
  align-items: center;
}

.reference-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  max-height: 318px;
  overflow: auto;
}

.reference-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #dfe5ef;
  border-radius: 6px;
  background: #ffffff;
}

.reference-card.disabled {
  opacity: 0.55;
}

.reference-index {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 2;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  color: #ffffff;
  line-height: 22px;
  text-align: center;
  border-radius: 11px;
  background: rgba(17, 24, 39, 0.72);
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 700;
}

.reference-thumb {
  display: block;
  width: 100%;
  height: 104px;
  background: #111827;
  object-fit: contain;
}

.reference-name {
  padding: 6px 7px;
  color: #606266;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reference-card-actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  padding: 0 6px 7px;
  border-top: 1px solid #eef2f7;
}

.reference-card-actions .el-button {
  margin: 0;
}

.danger-link {
  color: #f56c6c;
}

.task-model-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 17px;
}

.task-model-name {
  color: #1f2937;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-model-id {
  color: #8b95a5;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-image-strip {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 52px;
}

.task-image-strip img {
  width: 52px;
  height: 52px;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  background: #111827;
}

.task-image-strip img:hover {
  border-color: #409eff;
}

.task-image-more {
  min-width: 30px;
  height: 24px;
  padding: 0 7px;
  color: #4b5563;
  line-height: 24px;
  text-align: center;
  border-radius: 12px;
  background: #eef2f7;
  font-size: 12px;
  box-sizing: border-box;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  color: #6b7280;
  font-size: 12px;
}

.reference-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 92px;
  color: #9ca3af;
  background: #ffffff;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
}

.result-panel {
  position: sticky;
  top: 12px;
  align-self: start;
  width: 100%;
  max-width: none;
}

.image-grid {
  display: grid;
  gap: 10px;
}

.result-grid {
  grid-template-columns: repeat(auto-fill, minmax(172px, 1fr));
  max-height: 640px;
  overflow: auto;
  padding-right: 4px;
}

.asset-grid {
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
}

.image-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  background: #ffffff;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.asset-card {
  cursor: pointer;
}

.asset-card:hover {
  border-color: #b7cdf4;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.asset-card.selected {
  border-color: #409eff;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.2);
}

.asset-check {
  position: absolute;
  top: 7px;
  left: 8px;
  z-index: 2;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.88);
}

.image-thumb {
  display: block;
  width: 100%;
  height: auto;
  min-height: 150px;
  aspect-ratio: 1 / 1;
  background: #111827;
  object-fit: contain;
}

.image-meta {
  padding: 8px 8px 0;
  color: #303133;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.image-sub-meta {
  padding: 2px 8px 0;
  color: #9ca3af;
  font-size: 12px;
  line-height: 16px;
}

.image-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  flex-wrap: wrap;
  padding: 3px 8px 7px;
}

.image-actions .el-button {
  margin-left: 0;
}

.asset-search {
  width: 220px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #9ca3af;
  background: #f8fafc;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  font-size: 13px;
}

@media (max-width: 1180px) {
  .image-workspace {
    grid-template-columns: 1fr;
    max-width: none;
  }

  .result-panel {
    position: static;
    max-width: none;
  }
}

@media (max-width: 980px) {
  .page-head,
  .panel-title,
  .reference-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .image-workspace {
    grid-template-columns: 1fr;
  }

  .batch-layout {
    grid-template-columns: 1fr;
  }

  .capability-strip,
  .reference-panel {
    margin-left: 0;
  }

  .result-panel {
    position: static;
  }
}

@media (max-width: 640px) {
  .aigc-image-page {
    padding: 12px;
  }

  .mode-grid,
  .asset-grid,
  .result-grid,
  .reference-grid {
    grid-template-columns: 1fr;
  }

  .reference-url-row {
    grid-template-columns: 1fr;
  }

  .asset-search {
    width: 100%;
  }
}
</style>
